import React, { useMemo, useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import * as XLSX from 'xlsx';
import HoverPO from '../HoverPO';
import CdcrModal from '../cdcr/CdcrModal';

const LINE_STATUS_MENU_MAX = 150;
const LINE_STATUS_GUTTER = 8;

/** Keep a line-status menu inside the window. Open it above the row when the space below is too short. */
function placeLineStatusMenu(rect, menuMax = LINE_STATUS_MENU_MAX) {
    const spaceBelow = Math.max(0, window.innerHeight - rect.bottom - LINE_STATUS_GUTTER);
    const spaceAbove = Math.max(0, rect.top - LINE_STATUS_GUTTER);
    const openAbove = spaceBelow < menuMax && spaceAbove > spaceBelow;
    const available = Math.max(80, openAbove ? spaceAbove : spaceBelow);
    const maxHeight = Math.min(menuMax, available);
    let top = openAbove ? rect.top - maxHeight - 2 : rect.bottom + 2;
    const bottomLimit = window.innerHeight - LINE_STATUS_GUTTER;
    if (top + maxHeight > bottomLimit) {
        top = Math.max(LINE_STATUS_GUTTER, bottomLimit - maxHeight);
    }
    if (top < LINE_STATUS_GUTTER) top = LINE_STATUS_GUTTER;

    const width = Math.max(rect.width || 0, 160);
    let left = rect.left;
    const maxLeft = window.innerWidth - width - LINE_STATUS_GUTTER;
    if (left > maxLeft) left = Math.max(LINE_STATUS_GUTTER, maxLeft);
    if (left < LINE_STATUS_GUTTER) left = LINE_STATUS_GUTTER;

    return { top, left, width, maxHeight, openAbove };
}

/** Display quantities as whole numbers with no decimal point (e.g. 5000.000000000 → 5000). */
const formatQtyDisplay = (value) => {
    if (value == null || value === '') return '0';
    const num = Number(value);
    if (!Number.isFinite(num)) return String(value);
    return String(Math.trunc(num));
};

// Line Status Tags (fallback if not provided via props; display order)
const DEFAULT_LINE_STATUS_TAGS = [
    { label: 'Released', color: '#02f77c' },
    { label: 'Shipped', color: '#2D895F' },
    { label: 'In Kitting', color: '#CB5722' },
    { label: 'In Process', color: '#9288FF' },
    { label: 'In Testing', color: '#84E6F8' },
    { label: 'In Outgoing Inspection', color: '#B8B2E1' },
    { label: 'Not Released', color: '#F3DC6B' },
    { label: 'On Hold Cart', color: '#C56A00' },
    { label: 'Staging Cart', color: '#1DA1DB' },
    { label: 'Waiting On Components', color: '#A40202' },
    { label: 'PMA', color: '#594E9F' },
    { label: 'RMA', color: '#EB4F4F' },
    { label: 'ENG Review', color: '#625C5C' },
    { label: 'Charge', color: '#1d545e' }
];

const InlineOrderDropdown = ({
    line,
    inlineDropdownMode,
    expandedInlineOrders,
    orderDetailsData,
    inlineActiveView,
    renderInlineOrderHeader,
    orderFolderLink,
    onInlineViewChange,
    onInlineSwitchView,
    onInlineDualView,
    expandedInlineBomRows,
    onToggleInlineBomRow,
    lineStatusSelections = {},
    submittedLineStatuses = {},
    submittedOrderStatuses = {},
    orderStatusSelections = {},
    lineTagsToDeselect = {},
    lineStatusDropdownOpen = {},
    onToggleLineStatusDropdown,
    onHandleLineStatusToggle,
    onHandleLineTagDeselect,
    onHandleBulkLineTagDeselect,
    hasLineChangesToSubmit,
    onHandleLineStatusSubmit,
    lineStatusTags = [],
    contentPaddingClass = 'p-3',
    containerClassName = 'inline-order-details',
    tableClassName = 'table table-hover border rounded',
    dualViewOrderTableClassName = 'table table-hover border rounded inline-dual-order-lines-table',
    dualViewBomTableClassName = 'table table-hover border rounded dual-view-bom-table',
    commentsMap = {},
    onAddComment,
    onDeleteComment,
    onResolveThread,
    orderBulkModeEnabled = {},
    setOrderBulkModeEnabled,
    orderHasBulkChanges = {},
    setOrderHasBulkChanges,
    onBulkSubmit,
    onArchiveOrderLine,
    onArchiveBomPart,
    archivedOrderIds = new Set(),
    excludedAvailabilityPartsByOrder = {},
    onToggleExcludedAvailabilityPart,
    shouldRowBeOrange,
    orderData = [],
    dropdownColSpan = 14,
    dropdownTrailingCells = 2, // Number of trailing blank cells to keep aligned with right-side parent columns
    rowClassName = '',
    fullWidthExpandRow = false,
    expandRowMinWidth = '100%',
    portalPartLinkDropdowns = false,
    /** Live presence (optional). renderLinePresence(lineId) -> node shown next to the
     *  line status cell; lineStatusLockOwner(lineId) -> {name} when someone else holds it. */
    renderLinePresence,
    lineStatusLockOwner
}) => {
    const orderNum = line?.order?.ordernum || line?.ordernum;
    const orderKey = String(orderNum || '');
    const isInlineActive = inlineDropdownMode && expandedInlineOrders?.[orderNum];
    const orderDetails = orderDetailsData?.[orderNum];
    const excludedPartsForOrder = excludedAvailabilityPartsByOrder?.[orderKey] || {};
    const [pendingExcludedPartsForOrder, setPendingExcludedPartsForOrder] = useState({});
    const isOrange = shouldRowBeOrange ? shouldRowBeOrange(line) : false;

    useEffect(() => {
        setPendingExcludedPartsForOrder(
            excludedAvailabilityPartsByOrder?.[orderKey] ||
            excludedAvailabilityPartsByOrder?.[String(orderKey)] ||
            {}
        );
    }, [orderKey, excludedAvailabilityPartsByOrder]);
    
    // Order-specific bulk mode state
    const bulkModeEnabled = orderBulkModeEnabled[orderNum] || false;
    const hasBulkChanges = orderHasBulkChanges[orderNum] || false;
    
    // State for Order Lines column sorting: { [orderNum]: { column, direction } }
    const [orderLinesSort, setOrderLinesSort] = useState({});
    const currentOrderLinesSort = orderLinesSort[orderNum] || { column: 'shipDate', direction: 'ascending' };
    
    const handleOrderLinesSort = (column) => {
        setOrderLinesSort(prev => {
            const current = prev[orderNum] || { column: 'shipDate', direction: 'ascending' };
            const sameColumn = current.column === column;
            return {
                ...prev,
                [orderNum]: {
                    column,
                    direction: sameColumn ? (current.direction === 'ascending' ? 'descending' : 'ascending') : 'ascending'
                }
            };
        });
    };
    
    const getOrderLinesSortIcon = (column) => {
        if (currentOrderLinesSort.column !== column) return '';
        return currentOrderLinesSort.direction === 'ascending' ? ' ▲' : ' ▼';
    };
    
    const sortedOrderDetails = useMemo(() => {
        if (!orderDetails || !Array.isArray(orderDetails)) return orderDetails;
        const { column, direction } = currentOrderLinesSort;
        const asc = direction === 'ascending';
        return [...orderDetails].sort((a, b) => {
            let valA, valB;
            switch (column) {
                case 'line':
                    valA = Number(a.lineitem) || 0;
                    valB = Number(b.lineitem) || 0;
                    return asc ? valA - valB : valB - valA;
                case 'part':
                    valA = String(a.prcpart || '').toLowerCase();
                    valB = String(b.prcpart || '').toLowerCase();
                    return asc ? valA.localeCompare(valB) : valB.localeCompare(valA);
                case 'shipDate': {
                    const dateA = a.promisedate || '';
                    const dateB = b.promisedate || '';
                    if (!dateA && !dateB) return 0;
                    if (!dateA) return 1;
                    if (!dateB) return -1;
                    const dA = new Date(dateA);
                    const dB = new Date(dateB);
                    return asc ? dA - dB : dB - dA;
                }
                case 'shipQty':
                    valA = Number(a.balancedue) || 0;
                    valB = Number(b.balancedue) || 0;
                    return asc ? valA - valB : valB - valA;
                case 'qoh':
                    valA = Number(a.part?.qoh_cache) || 0;
                    valB = Number(b.part?.qoh_cache) || 0;
                    return asc ? valA - valB : valB - valA;
                case 'qoo':
                    valA = Number(a.part?.qoo_cache) || 0;
                    valB = Number(b.part?.qoo_cache) || 0;
                    return asc ? valA - valB : valB - valA;
                case 'qr':
                    valA = Number(a.part?.qr_cache) || 0;
                    valB = Number(b.part?.qr_cache) || 0;
                    return asc ? valA - valB : valB - valA;
                case 'qtyAvail': {
                    const qohA = Number(a.part?.qoh_cache) || 0, qrA = Number(a.part?.qr_cache) || 0;
                    const qohB = Number(b.part?.qoh_cache) || 0, qrB = Number(b.part?.qr_cache) || 0;
                    valA = qohA - qrA;
                    valB = qohB - qrB;
                    return asc ? valA - valB : valB - valA;
                }
                default:
                    return 0;
            }
        });
    }, [orderDetails, currentOrderLinesSort]);

    const visibleOrderDetails = useMemo(() => {
        if (!Array.isArray(sortedOrderDetails)) return [];
        return sortedOrderDetails.filter((item) => !archivedOrderIds.has(item.id));
    }, [sortedOrderDetails, archivedOrderIds]);
    
    const normalizeShippingLabel = (raw) =>
        String(typeof raw === 'string' ? raw : raw?.label || '').trim().toLowerCase();

    const isInShippingOrShippedLabel = (raw) => {
        const s = normalizeShippingLabel(raw);
        return s === 'in shipping' || s === 'ready to ship' || s === 'shipped';
    };

    const isStaggingCartLabel = (raw) => {
        const s = normalizeShippingLabel(raw);
        return s === 'staging cart' || s === 'stagging cart';
    };

    const isOnHoldCartLabel = (raw) => {
        const s = normalizeShippingLabel(raw);
        return s === 'on hold cart';
    };

    const formatLineStatusLabel = (raw) => {
        const label = String(typeof raw === 'string' ? raw : raw?.label || '').trim();
        if (isStaggingCartLabel(label)) return 'Staging Cart';
        return label;
    };

    const lineStatusLabelsEqual = (a, b) => {
        const la = normalizeShippingLabel(a);
        const lb = normalizeShippingLabel(b);
        const normalize = (s) => (s === 'stagging cart' ? 'staging cart' : s);
        return normalize(la) === normalize(lb);
    };

    const lineItemHasOrderStatusHold = (item) => {
        if (!item?.id) return false;
        const hold = ['cc hold', 'wire hold', 'credit hold', 'ppa'];
        const tags = [
            ...(submittedOrderStatuses[item.id] || []),
            ...(orderStatusSelections[item.id] || []),
        ];
        return tags.some((tag) => hold.includes(String(tag?.label || tag || '').toLowerCase()));
    };

    /** Per order line: green only when this line's saved/pending tags include In Shipping or Shipped. */
    const lineItemHasInShippingOrShipped = (item) => {
        if (!item?.id) return false;

        const lineId = item.id;

        if ((submittedLineStatuses[lineId] || []).some((t) => isInShippingOrShippedLabel(t))) return true;
        if ((lineStatusSelections[lineId] || []).some((t) => isInShippingOrShippedLabel(t))) return true;

        if ((item.lineStatuses || []).some((x) => isInShippingOrShippedLabel(typeof x === 'string' ? x : x?.label))) {
            return true;
        }

        if (item.lineItemStatus) {
            try {
                const parsed = JSON.parse(item.lineItemStatus);
                if (Array.isArray(parsed) && parsed.some((t) => isInShippingOrShippedLabel(typeof t === 'string' ? t : t?.label))) {
                    return true;
                }
            } catch {
                /* ignore */
            }
        }

        return false;
    };

    const lineItemHasStaggingCart = (item) => {
        if (!item?.id) return false;

        const lineId = item.id;

        if ((submittedLineStatuses[lineId] || []).some((t) => isStaggingCartLabel(t))) return true;
        if ((lineStatusSelections[lineId] || []).some((t) => isStaggingCartLabel(t))) return true;

        if ((item.lineStatuses || []).some((x) => isStaggingCartLabel(typeof x === 'string' ? x : x?.label))) {
            return true;
        }

        if (item.lineItemStatus) {
            try {
                const parsed = JSON.parse(item.lineItemStatus);
                if (Array.isArray(parsed) && parsed.some((t) => isStaggingCartLabel(typeof t === 'string' ? t : t?.label))) {
                    return true;
                }
            } catch {
                /* ignore */
            }
        }

        return false;
    };

    const lineItemHasOnHoldCart = (item) => {
        if (!item?.id) return false;

        const lineId = item.id;

        if ((submittedLineStatuses[lineId] || []).some((t) => isOnHoldCartLabel(t))) return true;
        if ((lineStatusSelections[lineId] || []).some((t) => isOnHoldCartLabel(t))) return true;

        if ((item.lineStatuses || []).some((x) => isOnHoldCartLabel(typeof x === 'string' ? x : x?.label))) {
            return true;
        }

        if (item.lineItemStatus) {
            try {
                const parsed = JSON.parse(item.lineItemStatus);
                if (Array.isArray(parsed) && parsed.some((t) => isOnHoldCartLabel(typeof t === 'string' ? t : t?.label))) {
                    return true;
                }
            } catch {
                /* ignore */
            }
        }

        return false;
    };

    const orderHasHoldInDropdown = useMemo(() => {
        const hold = ['cc hold', 'wire hold', 'credit hold', 'ppa'];
        const hasHoldTags = (lineId) => {
            if (!lineId) return false;
            const tags = [
                ...(submittedOrderStatuses[lineId] || []),
                ...(orderStatusSelections[lineId] || []),
            ];
            return tags.some((tag) => hold.includes(String(tag?.label || tag || '').toLowerCase()));
        };
        if (line?.id && hasHoldTags(line.id)) return true;
        if (visibleOrderDetails.some((item) => hasHoldTags(item.id))) return true;
        const orderNum = line?.order?.ordernum;
        if (orderNum && Array.isArray(orderData)) {
            return orderData.some(
                (ln) => String(ln?.order?.ordernum) === String(orderNum) && hasHoldTags(ln.id)
            );
        }
        return false;
    }, [line, visibleOrderDetails, submittedOrderStatuses, orderStatusSelections, orderData]);

    const orderHasPaidInDropdown = useMemo(() => {
        const hasPaidTag = (lineId) => {
            if (!lineId) return false;
            const tags = [
                ...(submittedOrderStatuses[lineId] || []),
                ...(orderStatusSelections[lineId] || []),
            ];
            return tags.some((tag) => String(tag?.label || tag || '').trim().toLowerCase() === 'paid');
        };
        if (line?.id && hasPaidTag(line.id)) return true;
        if (visibleOrderDetails.some((item) => hasPaidTag(item.id))) return true;
        const orderNum = line?.order?.ordernum;
        if (orderNum && Array.isArray(orderData)) {
            return orderData.some(
                (ln) => String(ln?.order?.ordernum) === String(orderNum) && hasPaidTag(ln.id)
            );
        }
        return false;
    }, [line, visibleOrderDetails, submittedOrderStatuses, orderStatusSelections, orderData]);

    const shouldRowBeGreenInline = (item) => {
        if (!item?.id) return false;
        // Same as Shipped: hold order statuses block green until Paid is also selected
        if (orderHasHoldInDropdown && !orderHasPaidInDropdown) return false;
        return (
            lineItemHasInShippingOrShipped(item) ||
            lineItemHasStaggingCart(item) ||
            lineItemHasOnHoldCart(item)
        );
    };
    
    // Debug logging for bulk mode
    useEffect(() => {
        console.log(`InlineOrderDropdown render for order ${orderNum}:`, {
            bulkModeEnabled,
            hasBulkChanges,
            orderBulkModeEnabled,
            orderHasBulkChanges
        });
    }, [orderNum, bulkModeEnabled, hasBulkChanges, orderBulkModeEnabled, orderHasBulkChanges]);
    
    // Aggregate all comments from all lines in this order
    const aggregatedComments = useMemo(() => {
        if (!orderDetails || !Array.isArray(orderDetails)) return [];
        const allComments = [];
        orderDetails.forEach((item) => {
            const backendComments = item.comment || [];
            const localComments = commentsMap[item.id] || [];
            const merged = [...backendComments, ...localComments];
            allComments.push(...merged);
        });
        // Sort by date/time (most recent first) - create a new array to avoid mutation
        return [...allComments].sort((a, b) => {
            const dateA = a.dateStr && a.timeStr ? new Date(`${a.dateStr} ${a.timeStr}`) : new Date(0);
            const dateB = b.dateStr && b.timeStr ? new Date(`${b.dateStr} ${b.timeStr}`) : new Date(0);
            return dateB - dateA;
        });
    }, [orderDetails, commentsMap]);
    
    // Get the first line ID for adding new comments (use first line as default)
    const firstLineId = useMemo(() => {
        if (!orderDetails || !Array.isArray(orderDetails) || orderDetails.length === 0) return null;
        return orderDetails[0].id;
    }, [orderDetails]);
    
    // Get latest comment text for display
    const latestComment = aggregatedComments.length > 0 ? aggregatedComments[0].comment : '';
    
    // Handler for adding comment from inline dropdown
    const handleInlineAddComment = (newComment, status = "pending") => {
        if (firstLineId && onAddComment) {
            onAddComment(firstLineId, newComment, status);
        }
    };
    
    // Handler for deleting comment from inline dropdown
    const handleInlineDeleteComment = (commentId) => {
        if (firstLineId && onDeleteComment) {
            onDeleteComment(firstLineId, commentId);
        }
    };
    
    // Handler for resolving comment from inline dropdown
    const handleInlineResolveThread = (commentId = null) => {
        if (firstLineId && onResolveThread) {
            onResolveThread(firstLineId, commentId);
        }
    };
    
    // Use provided tags or fallback to default tags - ensure we always have tags
    const availableTags = useMemo(() => {
        if (lineStatusTags && Array.isArray(lineStatusTags) && lineStatusTags.length > 0) {
            return lineStatusTags;
        }
        return DEFAULT_LINE_STATUS_TAGS;
    }, [lineStatusTags]);
    
    // State to track part link dropdowns
    const [partDropdownOpen, setPartDropdownOpen] = useState({});
    const [partDropdownPositions, setPartDropdownPositions] = useState({});
    const partTriggerRefs = useRef({});
    
    // State to track BOM component part link dropdowns
    const [bomPartDropdownOpen, setBomPartDropdownOpen] = useState({});
    const [bomPartDropdownPositions, setBomPartDropdownPositions] = useState({});
    const bomPartTriggerRefs = useRef({});
    
    // State to track line status dropdown positions
    const [lineStatusDropdownPositions, setLineStatusDropdownPositions] = useState({});
    const lineStatusTriggerRefs = useRef({});
    
    // State for bulk line status update
    const [bulkLineStatusDropdownOpen, setBulkLineStatusDropdownOpen] = useState(false);
    const [bulkLineStatusDropdownPosition, setBulkLineStatusDropdownPosition] = useState(null);
    const bulkLineStatusTriggerRef = useRef(null);
    const [pendingBulkTag, setPendingBulkTag] = useState(null); // Track pending bulk tag selection

    const formatPartNumber = (value) => {
        const partNumber = String(value || '');
        return partNumber.length >= 3 ? partNumber.substring(3) : partNumber || 'N/A';
    };

    const { aggregatedBomParts, bomPartsCount, parentPartIdMap } = useMemo(() => {
        if (!Array.isArray(orderDetails)) {
            return { aggregatedBomParts: [], bomPartsCount: 0, parentPartIdMap: new Map() };
        }

        const bomPartMap = new Map();
        const parentPartIdMapping = new Map(); // Map part number to part ID

        orderDetails.forEach((item) => {
            const lineShipQty = Number(item?.balancedue) || 0;
            const parentPart = item?.prcpart || '';
            const parentPartId = item?.part?.id;
            const bomParts = item?.bom?.bomParts || [];

            // Store mapping of parent part number to part ID
            if (parentPart && parentPartId) {
                parentPartIdMapping.set(parentPart, parentPartId);
            }

            bomParts.forEach((bom) => {
                const key = bom?.prcpart;
                if (!key) {
                    return;
                }

                const isArchived = !!bom?.archived;
                const qtyPerTop = Number(bom?.qty_per_top) || 0;
                const qtyOrder = isArchived ? 0 : qtyPerTop * lineShipQty;
                const part = bom?.part || {};
                const qohBom = Number(part?.qoh_cache) || 0;
                const qooBom = Number(part?.qoo_cache) || 0;
                const qrBom = Number(part?.qr_cache) || 0;
                const occurrence = { parentPart, bomPartId: bom?.id, archived: isArchived };

                if (bomPartMap.has(key)) {
                    const existing = bomPartMap.get(key);
                    existing.qtyOrder += qtyOrder;
                    if (parentPart) {
                        existing.parentParts.add(parentPart);
                    }
                    existing.occurrences.push(occurrence);
                } else {
                    bomPartMap.set(key, {
                        prcpart: key,
                        qtyPerTop,
                        qtyOrder,
                        qohBom,
                        qooBom,
                        qrBom,
                        qtyAvail: qohBom - qrBom,
                        parentParts: parentPart ? new Set([parentPart]) : new Set(),
                        occurrences: [occurrence],
                        part,
                    });
                }
            });
        });

        const aggregatedBomParts = Array.from(bomPartMap.values());
        return { aggregatedBomParts, bomPartsCount: aggregatedBomParts.length, parentPartIdMap: parentPartIdMapping };
    }, [orderDetails]);

    // Archive/Unarchive button for a single, unambiguous BOM part row (per-line BOM details tables).
    const renderBomArchiveCell = (bom) => {
        const isArchived = !!bom?.archived;

        return (
            <button
                type="button"
                className={`btn btn-sm ${isArchived ? 'btn-outline-success' : 'btn-outline-warning'}`}
                onClick={(e) => {
                    e.stopPropagation();
                    if (!onArchiveBomPart || !bom?.id) return;
                    onArchiveBomPart(bom.id, !isArchived, orderKey);
                }}
                disabled={!onArchiveBomPart || !bom?.id}
                title={isArchived ? 'Unarchive this BOM part' : 'Archive this BOM part'}
                style={{ fontSize: '0.7rem', padding: '2px 8px' }}
            >
                {isArchived ? 'Unarchive' : 'Archive'}
            </button>
        );
    };

    // Archive/Unarchive button for an aggregated (merged) BOM Parts row.
    // Targets the first underlying BOMParts id for that component.
    const renderAggregatedArchiveCell = (line) => {
        const target = (line.occurrences || [])[0];
        if (!target) {
            return <span className="text-muted">-</span>;
        }
        const isArchived = !!target.archived;

        return (
            <button
                type="button"
                className={`btn btn-sm ${isArchived ? 'btn-outline-success' : 'btn-outline-warning'}`}
                onClick={(e) => {
                    e.stopPropagation();
                    if (!onArchiveBomPart || !target.bomPartId) return;
                    onArchiveBomPart(target.bomPartId, !isArchived, orderKey);
                }}
                disabled={!onArchiveBomPart || !target.bomPartId}
                title={isArchived ? 'Unarchive this BOM part' : 'Archive this BOM part'}
                style={{ fontSize: '0.7rem', padding: '2px 8px' }}
            >
                {isArchived ? 'Unarchive' : 'Archive'}
            </button>
        );
    };

    // Customer Drawing Change Request (CDCR) for one order line; null when the form is closed.
    const [cdcrLine, setCdcrLine] = useState(null);

    const renderCdcrCell = (item) => (
        <button
            type="button"
            className="btn btn-link p-0"
            onClick={(e) => {
                e.stopPropagation();
                setCdcrLine(item);
            }}
            disabled={!item?.id}
            title="Customer Drawing Change Request"
            style={{ fontSize: '1.05rem', color: '#0071b9', textDecoration: 'none' }}
        >
            <i className="fa-solid fa-file-pen" aria-hidden="true"></i>
        </button>
    );

    const hasBomParts = bomPartsCount > 0;

    // State for BOM Parts column sorting: { [orderNum]: { column, direction } }
    const [bomPartsSort, setBomPartsSort] = useState({});
    const currentBomPartsSort = bomPartsSort[orderNum] || { column: 'qtyAvail', direction: 'ascending' };

    const handleBomPartsSort = (column) => {
        setBomPartsSort(prev => {
            const current = prev[orderNum] || { column: 'qtyAvail', direction: 'ascending' };
            const sameColumn = current.column === column;
            return {
                ...prev,
                [orderNum]: {
                    column,
                    direction: sameColumn ? (current.direction === 'ascending' ? 'descending' : 'ascending') : 'ascending'
                }
            };
        });
    };

    const getBomPartsSortIcon = (column) => {
        if (currentBomPartsSort.column !== column) return '';
        return currentBomPartsSort.direction === 'ascending' ? ' ▲' : ' ▼';
    };

    const sortedAggregatedBomParts = useMemo(() => {
        if (!aggregatedBomParts || !Array.isArray(aggregatedBomParts)) return aggregatedBomParts || [];
        const { column, direction } = currentBomPartsSort;
        const asc = direction === 'ascending';
        return [...aggregatedBomParts].sort((a, b) => {
            let valA, valB;
            switch (column) {
                case 'topLevel': {
                    const arrA = Array.from(a.parentParts || []);
                    const arrB = Array.from(b.parentParts || []);
                    valA = (arrA[0] || '').toString().toLowerCase();
                    valB = (arrB[0] || '').toString().toLowerCase();
                    return asc ? valA.localeCompare(valB) : valB.localeCompare(valA);
                }
                case 'componentPart':
                    valA = String(a.prcpart || '').toLowerCase();
                    valB = String(b.prcpart || '').toLowerCase();
                    return asc ? valA.localeCompare(valB) : valB.localeCompare(valA);
                case 'qtyOrder':
                    valA = Number(a.qtyOrder) || 0;
                    valB = Number(b.qtyOrder) || 0;
                    return asc ? valA - valB : valB - valA;
                case 'qoh':
                    valA = Number(a.qohBom) || 0;
                    valB = Number(b.qohBom) || 0;
                    return asc ? valA - valB : valB - valA;
                case 'qoo':
                    valA = Number(a.qooBom) || 0;
                    valB = Number(b.qooBom) || 0;
                    return asc ? valA - valB : valB - valA;
                case 'qr':
                    valA = Number(a.qrBom) || 0;
                    valB = Number(b.qrBom) || 0;
                    return asc ? valA - valB : valB - valA;
                case 'qtyAvail':
                    valA = Number(a.qtyAvail) || 0;
                    valB = Number(b.qtyAvail) || 0;
                    return asc ? valA - valB : valB - valA;
                default:
                    return 0;
            }
        });
    }, [aggregatedBomParts, currentBomPartsSort]);

    // Export Order Lines to Excel (current sorted view)
    const handleExportOrderLines = () => {
        if (!sortedOrderDetails || sortedOrderDetails.length === 0) {
            alert('No order lines to export.');
            return;
        }
        const headers = [['Line', 'Part', 'Ship Date', 'QOH', 'QOO', 'QR', 'Qty Avail']];
        const rows = sortedOrderDetails.map((item) => {
            const qoh = Number(item.part?.qoh_cache) || 0;
            const qr = Number(item.part?.qr_cache) || 0;
            const qtyAvail = qoh - qr;
            const partDisplay = String(item.prcpart || '').length >= 3 ? String(item.prcpart || '').substring(3) : (item.prcpart || 'N/A');
            return [
                item.lineitem,
                partDisplay,
                item.promisedate || 'N/A',
                qoh,
                Number(item.part?.qoo_cache) || 0,
                qr,
                qtyAvail
            ];
        });
        const worksheet = XLSX.utils.aoa_to_sheet([...headers, ...rows]);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Order Lines');
        XLSX.writeFile(workbook, `order_${orderNum}_lines.xlsx`);
    };

    // Export BOM Parts to Excel (current sorted view)
    const handleExportBomParts = () => {
        if (!sortedAggregatedBomParts || sortedAggregatedBomParts.length === 0) {
            alert('No BOM parts to export.');
            return;
        }
        const headers = [['S.No', 'Top Level Parts', 'Component Part', 'Qty/Order', 'QOH', 'QOO', 'QR', 'Qty Avail']];
        const rows = sortedAggregatedBomParts.map((entry, idx) => {
            const topLevel = Array.from(entry.parentParts || []).join('; ') || 'N/A';
            const partDisplay = String(entry.prcpart || '').length >= 3 ? String(entry.prcpart || '').substring(3) : (entry.prcpart || 'N/A');
            return [
                idx + 1,
                topLevel,
                partDisplay,
                entry.qtyOrder ?? 'N/A',
                entry.qohBom ?? 'N/A',
                entry.qooBom ?? 'N/A',
                entry.qrBom ?? 'N/A',
                entry.qtyAvail ?? 'N/A'
            ];
        });
        const worksheet = XLSX.utils.aoa_to_sheet([...headers, ...rows]);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'BOM Parts');
        XLSX.writeFile(workbook, `order_${orderNum}_bom_parts.xlsx`);
    };

    // Calculate part dropdown position when it opens
    useEffect(() => {
        const calculatePartPositions = () => {
            const newPositions = {};
            Object.keys(partDropdownOpen).forEach(itemId => {
                if (partDropdownOpen[itemId] && partTriggerRefs.current[itemId]) {
                    const trigger = partTriggerRefs.current[itemId];
                    const rect = trigger.getBoundingClientRect();
                    const viewportHeight = window.innerHeight;
                    const spaceBelow = viewportHeight - rect.bottom;
                    const spaceAbove = rect.top;
                    const dropdownHeight = 120; // Estimated height for 3 menu items
                    const minSpaceRequired = 100;
                    
                    const openAbove = spaceBelow < minSpaceRequired && spaceAbove > spaceBelow;
                    
                    let top;
                    if (openAbove) {
                        const estimatedHeight = dropdownHeight;
                        top = rect.top - estimatedHeight;
                        top = Math.max(5, top);
                    } else {
                        top = rect.bottom;
                    }
                    
                    newPositions[itemId] = {
                        top: top,
                        left: rect.left,
                        width: 'auto',
                        minWidth: '180px',
                        openAbove
                    };
                }
            });
            if (Object.keys(newPositions).length > 0) {
                setPartDropdownPositions(newPositions);
            }
        };

        const rafId = requestAnimationFrame(() => {
            calculatePartPositions();
        });
        
        window.addEventListener('resize', calculatePartPositions);
        
        return () => {
            cancelAnimationFrame(rafId);
            window.removeEventListener('resize', calculatePartPositions);
        };
    }, [partDropdownOpen]);

    // Calculate BOM part dropdown position when it opens
    useEffect(() => {
        const calculateBomPartPositions = () => {
            const newPositions = {};
            Object.keys(bomPartDropdownOpen).forEach(partKey => {
                if (bomPartDropdownOpen[partKey] && bomPartTriggerRefs.current[partKey]) {
                    const trigger = bomPartTriggerRefs.current[partKey];
                    const rect = trigger.getBoundingClientRect();
                    const viewportHeight = window.innerHeight;
                    const spaceBelow = viewportHeight - rect.bottom;
                    const spaceAbove = rect.top;
                    const dropdownHeight = 120; // Estimated height for 3 menu items
                    const minSpaceRequired = 100;
                    
                    const openAbove = spaceBelow < minSpaceRequired && spaceAbove > spaceBelow;
                    
                    let top;
                    if (openAbove) {
                        const estimatedHeight = dropdownHeight;
                        top = rect.top - estimatedHeight;
                        top = Math.max(5, top);
                    } else {
                        top = rect.bottom;
                    }
                    
                    newPositions[partKey] = {
                        top: top,
                        left: rect.left,
                        width: 'auto',
                        minWidth: '180px',
                        openAbove
                    };
                }
            });
            if (Object.keys(newPositions).length > 0) {
                setBomPartDropdownPositions(newPositions);
            }
        };

        const rafId = requestAnimationFrame(() => {
            calculateBomPartPositions();
        });
        
        window.addEventListener('resize', calculateBomPartPositions);
        
        return () => {
            cancelAnimationFrame(rafId);
            window.removeEventListener('resize', calculateBomPartPositions);
        };
    }, [bomPartDropdownOpen]);

    // Calculate line status dropdown position when it opens
    useEffect(() => {
        const calculateLineStatusPositions = () => {
            const newPositions = {};
            Object.keys(lineStatusDropdownOpen).forEach(itemId => {
                if (lineStatusDropdownOpen[itemId] && lineStatusTriggerRefs.current[itemId]) {
                    const trigger = lineStatusTriggerRefs.current[itemId];
                    newPositions[itemId] = placeLineStatusMenu(trigger.getBoundingClientRect());
                }
            });
            if (Object.keys(newPositions).length > 0) {
                setLineStatusDropdownPositions(newPositions);
            }
        };

        const rafId = requestAnimationFrame(() => {
            calculateLineStatusPositions();
        });
        
        window.addEventListener('resize', calculateLineStatusPositions);
        
        return () => {
            cancelAnimationFrame(rafId);
            window.removeEventListener('resize', calculateLineStatusPositions);
        };
    }, [lineStatusDropdownOpen, availableTags.length]);

    // Close part dropdown on scroll (similar to order status and line status dropdowns)
    useEffect(() => {
        const handleScroll = () => {
            if (Object.keys(partDropdownOpen).length > 0) {
                setPartDropdownOpen({});
            }
            if (Object.keys(bomPartDropdownOpen).length > 0) {
                setBomPartDropdownOpen({});
            }
        };

        window.addEventListener('scroll', handleScroll, true);
        
        return () => {
            window.removeEventListener('scroll', handleScroll, true);
        };
    }, [partDropdownOpen, bomPartDropdownOpen]);

    // Close bulk line status dropdown on scroll. Individual line status dropdowns are
    // closed by the parent page (Orders / Order Validation) in one idempotent handler:
    // calling the per-item TOGGLE from every InlineOrderDropdown instance fired it once
    // per rendered row, so an even number of calls re-opened the menu at its stale
    // fixed position (detached from the field).
    useEffect(() => {
        const handleScroll = (event) => {
            const target = event?.target;
            const isLineStatusDropdownScroll = target && target.closest && target.closest('.line-status-dropdown-menu');
            const isBulkDropdownScroll = target && target.closest && target.closest('.bulk-line-status-dropdown-menu');

            if (isLineStatusDropdownScroll || isBulkDropdownScroll) {
                return;
            }

            // Close bulk dropdown on scroll
            if (bulkLineStatusDropdownOpen) {
                setBulkLineStatusDropdownOpen(false);
            }
        };

        window.addEventListener('scroll', handleScroll, true);
        
        return () => {
            window.removeEventListener('scroll', handleScroll, true);
        };
    }, [bulkLineStatusDropdownOpen]);

    // Close part dropdown when clicking outside
    useEffect(() => {
        const hasPartOpen = Object.values(partDropdownOpen).some(Boolean);
        const hasBomOpen = Object.values(bomPartDropdownOpen).some(Boolean);
        if (!hasPartOpen && !hasBomOpen) return;

        const handleClickOutside = (event) => {
            const isPartTriggerClick = Object.values(partTriggerRefs.current).some(
                ref => ref && ref.contains(event.target)
            );
            const isBomTriggerClick = Object.values(bomPartTriggerRefs.current).some(
                ref => ref && ref.contains(event.target)
            );
            const isPartMenuClick = Array.from(document.querySelectorAll('.part-link-dropdown-menu')).some(
                el => el.contains(event.target)
            );
            const isBomMenuClick = Array.from(document.querySelectorAll('.bom-part-link-dropdown-menu')).some(
                el => el.contains(event.target)
            );

            if (!isPartTriggerClick && !isBomTriggerClick && !isPartMenuClick && !isBomMenuClick) {
                setPartDropdownOpen({});
                setBomPartDropdownOpen({});
            }
        };

        document.addEventListener('mousedown', handleClickOutside);

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [partDropdownOpen, bomPartDropdownOpen]);

    // Close bulk dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (bulkLineStatusDropdownOpen) {
                const isBulkTrigger = bulkLineStatusTriggerRef.current && bulkLineStatusTriggerRef.current.contains(event.target);
                const isBulkDropdown = event.target.closest('.bulk-line-status-dropdown-menu');
                
                if (!isBulkTrigger && !isBulkDropdown) {
                    setBulkLineStatusDropdownOpen(false);
                }
            }
        };

        if (bulkLineStatusDropdownOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [bulkLineStatusDropdownOpen]);

    const togglePartDropdown = (itemId, event) => {
        event.stopPropagation();
        setBomPartDropdownOpen({});
        setPartDropdownOpen(prev => (prev[itemId] ? {} : { [itemId]: true }));
    };

    const toggleBomPartDropdown = (partKey, event) => {
        event.stopPropagation();
        setPartDropdownOpen({});
        setBomPartDropdownOpen(prev => (prev[partKey] ? {} : { [partKey]: true }));
    };

    const partLinkDropdownZIndex = portalPartLinkDropdowns ? 1000005 : 10000;

    const mountPartLinkDropdown = (menu) => (
        portalPartLinkDropdowns ? createPortal(menu, document.body) : menu
    );

    const renderPartDropdown = (item) => {
        const itemId = item.id;
        const isOpen = partDropdownOpen[itemId];
        const position = partDropdownPositions[itemId];
        
        if (!isOpen || !position) {
            return null;
        }
        
        return mountPartLinkDropdown(
            <div
                className="part-link-dropdown-menu"
                style={{
                    position: 'fixed',
                    top: `${position.top}px`,
                    left: `${position.left}px`,
                    width: position.width,
                    minWidth: position.minWidth,
                    zIndex: partLinkDropdownZIndex,
                    backgroundColor: 'white',
                    border: '1px solid #dee2e6',
                    borderRadius: '8px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    marginTop: position.openAbove ? '0' : '2px',
                    padding: '4px 0'
                }}
                onClick={(e) => e.stopPropagation()}
            >
                <a 
                    className="dropdown-item d-flex align-items-center" 
                    href={`https://gigaflightinc.cetecerp.com/part/${item.part?.id}/inventory`} 
                    target="_blank" 
                    rel="noreferrer"
                    style={{ padding: '8px 16px', textDecoration: 'none', color: '#212529' }}
                    onMouseEnter={(e) => e.target.style.backgroundColor = '#f8f9fa'}
                    onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                >
                    <i className="fa fa-boxes me-2" aria-hidden="true" style={{ color: '#28a745' }}></i>Inventory
                </a>
                <a 
                    className="dropdown-item d-flex align-items-center" 
                    href={`https://gigaflightinc.cetecerp.com/part/${item.part?.id}/waterfall`} 
                    target="_blank" 
                    rel="noreferrer"
                    style={{ padding: '8px 16px', textDecoration: 'none', color: '#212529' }}
                    onMouseEnter={(e) => e.target.style.backgroundColor = '#f8f9fa'}
                    onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                >
                    <i className="fa fa-chart-line me-2" aria-hidden="true" style={{ color: '#ffc107' }}></i>Waterfall
                </a>
                <a 
                    className="dropdown-item d-flex align-items-center" 
                    href={`https://gigaflightinc.cetecerp.com/part/${item.part?.id}/pos`} 
                    target="_blank" 
                    rel="noreferrer"
                    style={{ padding: '8px 16px', textDecoration: 'none', color: '#212529' }}
                    onMouseEnter={(e) => e.target.style.backgroundColor = '#f8f9fa'}
                    onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                >
                    <i className="fa fa-shopping-cart me-2" aria-hidden="true" style={{ color: '#007bff' }}></i>Purchase Orders
                </a>
            </div>
        );
    };

    const renderBomPartDropdownByKey = (partKey, partId) => {
        
        if (!partKey || !partId) return null;
        
        const isOpen = bomPartDropdownOpen[partKey];
        const position = bomPartDropdownPositions[partKey];
        
        if (!isOpen || !position) {
            return null;
        }
        
        return mountPartLinkDropdown(
            <div
                className="bom-part-link-dropdown-menu"
                style={{
                    position: 'fixed',
                    top: `${position.top}px`,
                    left: `${position.left}px`,
                    width: position.width,
                    minWidth: position.minWidth,
                    zIndex: partLinkDropdownZIndex,
                    backgroundColor: 'white',
                    border: '1px solid #dee2e6',
                    borderRadius: '8px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    marginTop: position.openAbove ? '0' : '2px',
                    padding: '4px 0'
                }}
                onClick={(e) => e.stopPropagation()}
            >
                <a 
                    className="dropdown-item d-flex align-items-center" 
                    href={`https://gigaflightinc.cetecerp.com/part/${partId}/inventory`} 
                    target="_blank" 
                    rel="noreferrer"
                    style={{ padding: '8px 16px', textDecoration: 'none', color: '#212529' }}
                    onMouseEnter={(e) => e.target.style.backgroundColor = '#f8f9fa'}
                    onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                >
                    <i className="fa fa-boxes me-2" aria-hidden="true" style={{ color: '#28a745' }}></i>Inventory
                </a>
                <a 
                    className="dropdown-item d-flex align-items-center" 
                    href={`https://gigaflightinc.cetecerp.com/part/${partId}/waterfall`} 
                    target="_blank" 
                    rel="noreferrer"
                    style={{ padding: '8px 16px', textDecoration: 'none', color: '#212529' }}
                    onMouseEnter={(e) => e.target.style.backgroundColor = '#f8f9fa'}
                    onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                >
                    <i className="fa fa-chart-line me-2" aria-hidden="true" style={{ color: '#ffc107' }}></i>Waterfall
                </a>
                <a 
                    className="dropdown-item d-flex align-items-center" 
                    href={`https://gigaflightinc.cetecerp.com/part/${partId}/pos`} 
                    target="_blank" 
                    rel="noreferrer"
                    style={{ padding: '8px 16px', textDecoration: 'none', color: '#212529' }}
                    onMouseEnter={(e) => e.target.style.backgroundColor = '#f8f9fa'}
                    onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                >
                    <i className="fa fa-shopping-cart me-2" aria-hidden="true" style={{ color: '#007bff' }}></i>Purchase Orders
                </a>
            </div>
        );
    };

    const renderBomPartDropdown = (entry) => {
        const partKey = entry.prcpart || entry.part?.id;
        return renderBomPartDropdownByKey(partKey, entry.part?.id);
    };

    if (!orderNum || !isInlineActive || !orderDetails) {
        return null;
    }

    const renderStatusBadges = (itemId) => (
        <>
            {(lineStatusSelections[`${itemId}`] || []).map((tag, tagIndex) => (
                <span
                    key={tagIndex}
                    className="badge"
                    style={{
                        backgroundColor: tag.color,
                        color: 'white',
                        fontSize: '0.9rem',
                        padding: '6px 12px',
                        borderRadius: '14px',
                        lineHeight: 1,
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '6px'
                    }}
                >
                    {tag.label}
                    <button
                        type="button"
                        style={{
                            background: 'none',
                            border: 'none',
                            color: 'white',
                            fontSize: '14px',
                            fontWeight: 'bold',
                            padding: '0',
                            margin: '0 0 0 4px',
                            width: '18px',
                            height: '18px',
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            lineHeight: '1'
                        }}
                        onClick={(e) => {
                            e.stopPropagation();
                            onHandleLineStatusToggle(`${itemId}`, tag, orderNum);
                        }}
                    >
                        ×
                    </button>
                </span>
            ))}

            {(submittedLineStatuses[`${itemId}`] || []).map((tag, tagIndex) => {
                const isMarkedForDeselection = (lineTagsToDeselect[`${itemId}`] || []).some(t => t.label === tag.label);
                return (
                    <span
                        key={`submitted-${tagIndex}`}
                        className={`badge${isMarkedForDeselection ? ' line-status-tag-pending-removal' : ''}`}
                        style={{
                            backgroundColor: isMarkedForDeselection ? "#9ca3af" : tag.color,
                            color: "white",
                            fontSize: "0.9rem",
                            padding: "6px 12px",
                            borderRadius: "14px",
                            cursor: "pointer",
                            opacity: 1,
                            lineHeight: 1,
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '6px'
                        }}
                        onClick={(e) => {
                            e.stopPropagation();
                            if (bulkModeEnabled && onHandleBulkLineTagDeselect) {
                                onHandleBulkLineTagDeselect(`${itemId}`, tag, orderNum);
                            } else {
                                onHandleLineTagDeselect(`${itemId}`, tag);
                            }
                        }}
                        title={isMarkedForDeselection ? "Pending removal — click to keep, or Save to remove" : "Click to mark for removal (Save to confirm)"}
                    >
                        {formatLineStatusLabel(tag)}
                    </span>
                );
            })}

            {(lineStatusSelections[`${itemId}`] || []).length === 0 &&
                (submittedLineStatuses[`${itemId}`] || []).length === 0 && (
                    <span className="text-muted" style={{ fontSize: '0.95rem' }}>Select tags...</span>
                )}
        </>
    );

    const renderStatusDropdown = (itemId) => {
        const isOpen = lineStatusDropdownOpen[`${itemId}`];
        const position = lineStatusDropdownPositions[itemId];
        
        if (!isOpen || !position) {
            return null;
        }
        
        return createPortal(
            <div
                className="line-status-dropdown-menu"
                style={{
                    position: 'fixed',
                    top: `${position.top}px`,
                    left: `${position.left}px`,
                    width: `${position.width}px`,
                    zIndex: 9999,
                    backgroundColor: 'white',
                    border: '1px solid #ced4da',
                    borderRadius: '4px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                    maxHeight: `${position.maxHeight || LINE_STATUS_MENU_MAX}px`,
                    overflowY: 'auto',
                    marginTop: 0
                }}
                onClick={(e) => e.stopPropagation()}
            >
                {availableTags.map((tag, tagIndex) => {
                    const isMarkedForDeselection = (lineTagsToDeselect[`${itemId}`] || []).some(
                        t => lineStatusLabelsEqual(t.label, tag.label)
                    );
                    const isSelected = (
                        (lineStatusSelections[`${itemId}`] || []).some(
                            selection => lineStatusLabelsEqual(selection.label, tag.label)
                        ) || (
                            (submittedLineStatuses[`${itemId}`] || []).some(
                                selection => lineStatusLabelsEqual(selection.label, tag.label)
                            ) && !isMarkedForDeselection
                        )
                    );
                    return (
                        <div
                            key={tagIndex}
                            className="p-2 d-flex align-items-center"
                            style={{
                                cursor: 'pointer',
                                backgroundColor: isSelected ? '#f8f9fa' : 'white',
                                borderBottom: '1px solid #f1f1f1',
                                fontSize: '12px'
                            }}
                            onClick={(e) => {
                                e.stopPropagation();
                                onHandleLineStatusToggle(`${itemId}`, tag, orderNum);
                            }}
                        >
                            <div
                                className="color-indicator me-2"
                                style={{
                                    width: '10px',
                                    height: '10px',
                                    borderRadius: '2px',
                                    backgroundColor: tag.color,
                                    border: '1px solid rgba(0, 0, 0, 0.3)',
                                    display: 'inline-block',
                                    flexShrink: 0,
                                    minWidth: '10px',
                                    minHeight: '10px'
                                }}
                                title={`Color: ${tag.color}`}
                            ></div>
                            <span style={{ fontSize: '0.75rem' }}>
                                {tag.label}
                            </span>
                            {isSelected && (
                                <span className="ms-auto" style={{ fontSize: '12px' }}>✓</span>
                            )}
                        </div>
                    );
                })}
            </div>,
            document.body
        );
    };

    // Handler for bulk line status selection - stores tag for confirmation, doesn't apply yet
    const handleBulkLineStatusToggle = (tag) => {
        // Store the selected tag as pending - don't apply it yet
        setPendingBulkTag(tag);
        // Close bulk dropdown
        setBulkLineStatusDropdownOpen(false);
    };

    // Handler for bulk confirmation - applies and saves tag to all lines
    const handleBulkLineStatusConfirm = async () => {
        if (!pendingBulkTag || !orderDetails || !Array.isArray(orderDetails)) return;
        
        const tag = pendingBulkTag;
        const linesToUpdate = [];
        
        // Apply the tag to all order lines
        orderDetails.forEach((item) => {
            const itemId = `${item.id}`;
            // Check if tag is already selected for this line
            const currentSelections = lineStatusSelections[itemId] || [];
            const submittedSelections = submittedLineStatuses[itemId] || [];
            const isAlreadySelected = currentSelections.some(selection => selection.label === tag.label) ||
                                     submittedSelections.some(selection => selection.label === tag.label);
            
            // Only add if not already selected
            if (!isAlreadySelected) {
                onHandleLineStatusToggle(itemId, tag);
                linesToUpdate.push(itemId);
            }
        });
        
        // Clear pending tag
        setPendingBulkTag(null);
        
        // Auto-submit/save for all lines that were updated
        // Use setTimeout to ensure state updates are processed first
        setTimeout(() => {
            linesToUpdate.forEach((itemId) => {
                if (hasLineChangesToSubmit && hasLineChangesToSubmit(itemId)) {
                    onHandleLineStatusSubmit(itemId);
                }
            });
        }, 100);
    };

    // Toggle bulk dropdown
    const toggleBulkLineStatusDropdown = (event) => {
        event.stopPropagation();
        if (bulkLineStatusTriggerRef.current) {
            const rect = bulkLineStatusTriggerRef.current.getBoundingClientRect();
            const placed = placeLineStatusMenu(rect);
            setBulkLineStatusDropdownPosition({
                ...placed,
                width: Math.max(placed.width, 200),
            });
        }
        setBulkLineStatusDropdownOpen(prev => !prev);
    };

    const renderLineStatusHeaderControls = () => (
        <div
            className="d-flex align-items-center justify-content-center"
            style={{ whiteSpace: 'nowrap', gap: '2px', lineHeight: 1, flexWrap: 'nowrap' }}
        >
            <span
                ref={bulkLineStatusTriggerRef}
                role="button"
                tabIndex={0}
                style={{ cursor: 'pointer' }}
                title="Select tag for all lines"
                onClick={toggleBulkLineStatusDropdown}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        toggleBulkLineStatusDropdown(e);
                    }
                }}
            >
                Line Status
            </span>
            <input
                className="m-0"
                type="checkbox"
                id={`bulkModeCheckbox-${orderNum}`}
                checked={bulkModeEnabled}
                onChange={(e) => {
                    if (setOrderBulkModeEnabled) {
                        setOrderBulkModeEnabled(prev => ({
                            ...prev,
                            [orderNum]: e.target.checked
                        }));
                    }
                    if (!e.target.checked && setOrderHasBulkChanges) {
                        setOrderHasBulkChanges(prev => ({
                            ...prev,
                            [orderNum]: false
                        }));
                    }
                }}
                style={{
                    cursor: 'pointer',
                    width: '12px',
                    height: '12px',
                    minWidth: '12px',
                    minHeight: '12px',
                    borderRadius: 0,
                    aspectRatio: '1 / 1',
                    appearance: 'auto',
                    WebkitAppearance: 'checkbox',
                    MozAppearance: 'checkbox',
                    margin: 0,
                    display: 'block',
                    alignSelf: 'center',
                    flexShrink: 0
                }}
                title="Enable bulk mode"
            />
            {bulkModeEnabled && (
                <button
                    type="button"
                    className={`btn btn-sm ${hasBulkChanges ? 'btn-success' : 'btn-secondary'}`}
                    style={{
                        minWidth: '24px',
                        height: '12px',
                        padding: '0 2px',
                        fontSize: '6px',
                        lineHeight: 1,
                        borderRadius: '2px',
                        cursor: hasBulkChanges ? 'pointer' : 'not-allowed',
                        opacity: hasBulkChanges ? 1 : 0.6,
                        flexShrink: 0
                    }}
                    disabled={!hasBulkChanges}
                    onClick={(e) => {
                        e.stopPropagation();
                        if (hasBulkChanges) {
                            onBulkSubmit && onBulkSubmit(orderNum);
                        }
                    }}
                    title={hasBulkChanges ? 'Save all bulk changes' : 'No bulk changes'}
                >
                    Save
                </button>
            )}
            {pendingBulkTag && (
                <button
                    type="button"
                    className="btn btn-success btn-sm"
                    style={{
                        minWidth: '12px',
                        height: '12px',
                        padding: 0,
                        fontSize: '6px',
                        lineHeight: 1,
                        borderRadius: '2px',
                        cursor: 'pointer',
                        flexShrink: 0
                    }}
                    onClick={(e) => {
                        e.stopPropagation();
                        handleBulkLineStatusConfirm();
                    }}
                    title={`Apply "${pendingBulkTag.label}" to all lines`}
                >
                    ✓
                </button>
            )}
        </div>
    );

    // Render bulk line status dropdown
    const renderBulkLineStatusDropdown = () => {
        if (!bulkLineStatusDropdownOpen || !bulkLineStatusDropdownPosition) {
            return null;
        }
        
        // Ensure we have tags to display - always use DEFAULT_LINE_STATUS_TAGS as fallback
        const tagsToDisplay = (availableTags && Array.isArray(availableTags) && availableTags.length > 0) 
            ? availableTags 
            : DEFAULT_LINE_STATUS_TAGS;
        
        // Always show dropdown with tags - if tagsToDisplay is empty, use default
        const finalTags = (tagsToDisplay && Array.isArray(tagsToDisplay) && tagsToDisplay.length > 0) 
            ? tagsToDisplay 
            : DEFAULT_LINE_STATUS_TAGS;
        
        return createPortal(
            <div
                className="bulk-line-status-dropdown-menu"
                style={{
                    position: 'fixed',
                    top: `${bulkLineStatusDropdownPosition.top}px`,
                    left: `${bulkLineStatusDropdownPosition.left}px`,
                    width: `${Math.max(bulkLineStatusDropdownPosition.width || 200, 200)}px`,
                    minWidth: '200px',
                    zIndex: 10000,
                    backgroundColor: 'white',
                    border: '1px solid #ced4da',
                    borderRadius: '4px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                    maxHeight: `${bulkLineStatusDropdownPosition.maxHeight || LINE_STATUS_MENU_MAX}px`,
                    overflowY: 'auto',
                    marginTop: 0,
                    padding: '4px 0'
                }}
                onClick={(e) => e.stopPropagation()}
            >
                {finalTags.map((tag, tagIndex) => (
                    <div
                        key={tagIndex}
                        className="p-2 d-flex align-items-center"
                        style={{
                            cursor: 'pointer',
                            backgroundColor: 'white',
                            borderBottom: '1px solid #f1f1f1',
                            fontSize: '12px'
                        }}
                        onClick={(e) => {
                            e.stopPropagation();
                            handleBulkLineStatusToggle(tag);
                        }}
                        onMouseEnter={(e) => e.target.style.backgroundColor = '#f8f9fa'}
                        onMouseLeave={(e) => e.target.style.backgroundColor = 'white'}
                    >
                        <div
                            className="color-indicator me-2"
                            style={{
                                width: '10px',
                                height: '10px',
                                borderRadius: '2px',
                                backgroundColor: tag.color,
                                border: '1px solid rgba(0, 0, 0, 0.3)',
                                display: 'inline-block',
                                flexShrink: 0,
                                minWidth: '10px',
                                minHeight: '10px'
                            }}
                            title={`Color: ${tag.color}`}
                        ></div>
                        <span style={{ fontSize: '0.75rem' }}>
                            {tag.label}
                        </span>
                    </div>
                ))}
            </div>,
            document.body
        );
    };

    const getBomPartQtyAvail = (bom) => {
        const part = bom?.part || {};
        return (Number(part.qoh_cache) || 0) - (Number(part.qr_cache) || 0);
    };

    /** BOM details under Action: negative QTY AVAIL rows first (worst shortages on top). */
    const sortBomPartsNegativeQtyAvailFirst = (bomParts) => {
        if (!Array.isArray(bomParts) || bomParts.length === 0) return [];
        return [...bomParts].sort((a, b) => {
            const qtyA = getBomPartQtyAvail(a);
            const qtyB = getBomPartQtyAvail(b);
            const aNegative = qtyA < 0;
            const bNegative = qtyB < 0;
            if (aNegative !== bNegative) {
                return aNegative ? -1 : 1;
            }
            return qtyA - qtyB;
        });
    };

    const renderBomDetails = (bomParts) => {
        const sortedBomParts = sortBomPartsNegativeQtyAvailFirst(bomParts);

        return (
        <table className="table gf-table border rounded mt-2 inline-bom-details-table" style={{ width: 'max-content' }}>
            <thead className="table-light">
                <tr className="text-center">
                    <th style={{ ...sharedHeaderStyle, width: '60px', minWidth: '60px', maxWidth: '60px' }}>S.No</th>
                    <th style={sharedHeaderStyle}>Part</th>
                    <th style={sharedHeaderStyle}>Qty/Top</th>
                    <th style={sharedHeaderStyle}>QOH</th>
                    <th style={sharedHeaderStyle}>QOO</th>
                    <th style={sharedHeaderStyle}>QR</th>
                    <th style={sharedHeaderStyle}>Qty Avail</th>
                    <th className="exclude-col" style={{ ...sharedHeaderStyle }}>Exclude</th>
                    <th style={sharedHeaderStyle}>Archive</th>
                </tr>
            </thead>
            <tbody>
                {sortedBomParts.map((bom, i) => {
                    const part = bom.part || {};
                    const qohBom = Number(part.qoh_cache) || 0;
                    const qooBom = Number(part.qoo_cache) || 0;
                    const qrBom = Number(part.qr_cache) || 0;
                    const qtyInHandBom = getBomPartQtyAvail(bom);
                    const isBomArchived = !!bom.archived;

                    return (
                        <tr key={i} style={{ backgroundColor: '#E6F2FF', ...(isBomArchived ? { opacity: 0.5, textDecoration: 'line-through' } : {}) }}>
                            <td style={{ width: '60px', minWidth: '60px', maxWidth: '60px' }}>{i + 1}</td>
                            <td>
                                {(() => {
                                    const partNumber = bom.prcpart || '';
                                    const displayPart = partNumber.length >= 3 ? partNumber.substring(3) : partNumber || 'N/A';
                                    const componentPartId = bom.part?.id;

                                    const bomPartKey = `bom-details-${i}-${partNumber || componentPartId || 'part'}`;
                                    
                                    return componentPartId ? (
                                         <div className="d-flex justify-content-between align-items-center">
                                            <span>{displayPart}</span>
                                            <div className="d-inline-block ms-2">
                                                <button
                                                    ref={el => bomPartTriggerRefs.current[bomPartKey] = el}
                                                    className="btn btn-sm btn-outline-primary"
                                                    type="button"
                                                    title="View Part Details"
                                                    style={{
                                                        padding: '2px 6px',
                                                        borderRadius: '4px',
                                                        border: '1px solid #0078d4',
                                                        backgroundColor: '#f8f9fa',
                                                        fontSize: '12px'
                                                    }}
                                                    onClick={(e) => toggleBomPartDropdown(bomPartKey, e)}
                                                 >
                                            <i className="fa fa-link" aria-hidden="true" style={{ fontSize: '0.8rem', color: '#0078d4' }}></i>
                                                </button>
                                            </div>
                                            {renderBomPartDropdownByKey(bomPartKey, componentPartId)}
                                        </div>
                                    ) : (
                                        displayPart
                                    );
                                })()}
                            </td>
                            <td>{bom.qty_per_top}</td>
                            <td>{qohBom}</td>
                            <td>
                                <div className="d-flex align-items-center justify-content-center">
                                    <span>{qooBom}</span>
                                    {qooBom > 0 && (
                                        <a
                                            href={`https://gigaflightinc.cetecerp.com/part/${bom.part?.id}/pos`}
                                            target="_blank"
                                            rel="noreferrer"
                                            title="purchase orders"
                                            className="ms-2"
                                        >
                                            <i className="fa fa-shopping-cart text-primary" aria-hidden="true" style={{ fontSize: '14px' }}></i>
                                        </a>
                                    )}
                                </div>
                            </td>
                            <td>{qrBom}</td>
                            <td>
                                <span className={qtyInHandBom < 0 ? 'bg-danger text-white px-2 py-1 rounded' : ''}>
                                    {qtyInHandBom}
                                </span>
                            </td>
                            <td className="exclude-col">
                                {qtyInHandBom < 0 ? (
                                    <div className="d-flex align-items-center justify-content-center" style={{ gap: '8px' }}>
                                        <button
                                            type="button"
                                            className="btn btn-sm"
                                            onClick={() => {
                                                setPendingExcludedPartsForOrder((prev) => ({
                                                    ...prev,
                                                    [bom.prcpart]: !prev[bom.prcpart],
                                                }));
                                            }}
                                            title="Select/deselect exclusion"
                                            style={{
                                                width: '16px',
                                                height: '16px',
                                                minWidth: '16px',
                                                padding: 0,
                                                borderRadius: '50%',
                                                border: '1px solid #0d6efd',
                                                backgroundColor: pendingExcludedPartsForOrder[bom.prcpart] ? '#0d6efd' : 'white',
                                                lineHeight: 1,
                                                fontSize: '10px',
                                                color: 'transparent'
                                            }}
                                        >
                                            &nbsp;
                                        </button>
                                        <button
                                            type="button"
                                            className="btn btn-outline-success btn-sm"
                                            style={{ minWidth: '34px', height: '20px', padding: '0 6px', lineHeight: 1, fontSize: '10px', cursor: 'pointer' }}
                                            title="Save exclusion"
                                            disabled={!onToggleExcludedAvailabilityPart || !!excludedPartsForOrder[bom.prcpart] === !!pendingExcludedPartsForOrder[bom.prcpart]}
                                            onClick={() => {
                                                if (!onToggleExcludedAvailabilityPart) return;
                                                onToggleExcludedAvailabilityPart(orderKey, bom.prcpart, !!pendingExcludedPartsForOrder[bom.prcpart]);
                                            }}
                                        >
                                            Save
                                        </button>
                                    </div>
                                ) : (
                                    <span className="text-muted">-</span>
                                )}
                            </td>
                            <td>
                                {renderBomArchiveCell(bom)}
                            </td>
                        </tr>
                    );
                })}
            </tbody>
        </table>
        );
    };

    const sharedHeaderStyle = {
        whiteSpace: 'nowrap'
    };

    const bomHeaderStyle = (minWidth) => ({
        minWidth,
        ...sharedHeaderStyle
    });

    const bomCellStyle = {
        padding: '8px 10px',
        fontSize: '1.1rem',
        lineHeight: 1.3
    };

    const orderLineHeaderStyle = (width) => ({
        width,
        ...sharedHeaderStyle
    });

    const dualViewOrderLineHeaderStyle = (width) => ({
        ...(width ? { width } : {}),
        whiteSpace: 'normal',
        wordWrap: 'break-word',
        overflowWrap: 'break-word',
        lineHeight: 1.15,
        verticalAlign: 'middle'
    });

    const orderLineCellStyle = {
        padding: '8px 10px',
        fontSize: '1.1rem',
        lineHeight: 1.3
    };

    const renderAggregatedBomTable = (className = tableClassName) => (
        <table className={`${className} gf-table inline-bom-table`} style={{ width: 'max-content' }}>
            <thead className="table-light">
                    <tr className="text-center">
                    <th className="inline-bom-col-sno" style={sharedHeaderStyle}>S.No</th>
                    <th className="inline-bom-col-top" style={{ ...sharedHeaderStyle, cursor: 'pointer' }} onClick={() => handleBomPartsSort('topLevel')}>{`Top level parts${getBomPartsSortIcon('topLevel')}`}</th>
                    <th className="inline-bom-col-component" style={{ ...sharedHeaderStyle, cursor: 'pointer' }} onClick={() => handleBomPartsSort('componentPart')}>{`Component part${getBomPartsSortIcon('componentPart')}`}</th>
                    <th className="inline-bom-col-numeric" style={{ ...sharedHeaderStyle, cursor: 'pointer' }} onClick={() => handleBomPartsSort('qtyOrder')}>{`Qty/Order${getBomPartsSortIcon('qtyOrder')}`}</th>
                    <th className="inline-bom-col-numeric" style={{ ...sharedHeaderStyle, cursor: 'pointer' }} onClick={() => handleBomPartsSort('qoh')}>{`QOH${getBomPartsSortIcon('qoh')}`}</th>
                    <th className="inline-bom-col-numeric" style={{ ...sharedHeaderStyle, cursor: 'pointer' }} onClick={() => handleBomPartsSort('qoo')}>{`QOO${getBomPartsSortIcon('qoo')}`}</th>
                    <th className="inline-bom-col-numeric" style={{ ...sharedHeaderStyle, cursor: 'pointer' }} onClick={() => handleBomPartsSort('qr')}>{`QR${getBomPartsSortIcon('qr')}`}</th>
                    <th className="inline-bom-col-numeric" style={{ ...sharedHeaderStyle, cursor: 'pointer' }} onClick={() => handleBomPartsSort('qtyAvail')}>{`Qty Avail${getBomPartsSortIcon('qtyAvail')}`}</th>
                    <th className="inline-bom-col-numeric exclude-col" style={{ ...sharedHeaderStyle }}>Exclude</th>
                    <th className="inline-bom-col-numeric" style={{ ...sharedHeaderStyle }}>Archive</th>
                </tr>
            </thead>
            <tbody className="text-center">
                {hasBomParts ? (
                    sortedAggregatedBomParts.map((entry, idx) => {
                        const parentPartsArray = Array.from(entry.parentParts || []);
                        return (
                            <tr key={`${entry.prcpart}-${idx}`} style={{ backgroundColor: '#E6F2FF' }}>
                                <td style={bomCellStyle}>{idx + 1}</td>
                                <td style={bomCellStyle}>
                                    {parentPartsArray.length > 0 ? (
                                        parentPartsArray.map((parent, parentIdx) => {
                                            // Look up part ID from orderDetails
                                            const parentPartId = parentPartIdMap.get(parent) || 
                                                (Array.isArray(orderDetails) ? orderDetails.find(item => item?.prcpart === parent)?.part?.id : null);
                                            const formattedPartNumber = formatPartNumber(parent);
                                            
                                            return (
                                                <div key={parentIdx}>
                                                    <>
                                                        {parentPartsArray.length > 1 && (
                                                            <span className="fw-bold text-primary me-1">
                                                                {parentIdx + 1})
                                                            </span>
                                                        )}
                                                        {parentPartId ? (
                                                            <>
                                                                <span>{formattedPartNumber}</span>
                                                                <span className="ms-2 d-inline-block">
                                                                    <button
                                                                        ref={el => bomPartTriggerRefs.current[`top-level-${idx}-${parentIdx}-${parentPartId}`] = el}
                                                                        className="btn btn-sm btn-outline-primary"
                                                                        type="button"
                                                                        title="View Part Details"
                                                                        style={{
                                                                            padding: '2px 6px',
                                                                            borderRadius: '4px',
                                                                            border: '1px solid #0078d4',
                                                                            backgroundColor: '#f8f9fa',
                                                                            fontSize: '12px'
                                                                        }}
                                                                        onClick={(e) => toggleBomPartDropdown(`top-level-${idx}-${parentIdx}-${parentPartId}`, e)}
                                                                    >
                                                                        <i className="fa fa-link" aria-hidden="true" style={{ fontSize: '0.8rem', color: '#0078d4' }}></i>
                                                                    </button>
                                                                </span>
                                                                {renderBomPartDropdownByKey(`top-level-${idx}-${parentIdx}-${parentPartId}`, parentPartId)}
                                                            </>
                                                        ) : (
                                                            <span>{formattedPartNumber}</span>
                                                        )}
                                                    </>
                                                </div>
                                            );
                                        })
                                    ) : (
                                        <span className="text-muted">N/A</span>
                                    )}
                                </td>
                                <td className="inline-bom-col-component" style={bomCellStyle}>
                                    <div className="d-flex justify-content-between align-items-center">
                                        <span>
                                            {formatPartNumber(entry.prcpart)}
                                        </span>
                                        {entry.part?.id && (
                                            <div className="d-inline-block">
                                                <button
                                                    ref={el => bomPartTriggerRefs.current[entry.prcpart || entry.part.id] = el}
                                                    className="btn btn-sm btn-outline-primary"
                                                    type="button"
                                                    title="View Part Details"
                                                    style={{
                                                        padding: '2px 6px',
                                                        borderRadius: '4px',
                                                        border: '1px solid #0078d4',
                                                        backgroundColor: '#f8f9fa',
                                                        fontSize: '12px'
                                                    }}
                                                    onClick={(e) => toggleBomPartDropdown(entry.prcpart || entry.part.id, e)}
                                                >
                                                    <i className="fa fa-link" aria-hidden="true" style={{ fontSize: '0.8rem', color: '#0078d4' }}></i>
                                                </button>
                                            </div>
                                        )}
                                        {renderBomPartDropdown(entry)}
                                    </div>
                                </td>
                                <td className="inline-bom-col-numeric" style={bomCellStyle}>{formatQtyDisplay(entry.qtyOrder)}</td>
                                <td className="inline-bom-col-numeric" style={bomCellStyle}>{entry.qohBom}</td>
                                <td className="inline-bom-col-numeric" style={bomCellStyle}>
                                    <div className="d-flex align-items-center justify-content-center">
                                        <span>{entry.qooBom}</span>
                                        {entry.qooBom > 0 && (
                                            <a
                                                href={`https://gigaflightinc.cetecerp.com/part/${entry.part?.id}/pos`}
                                                target="_blank"
                                                rel="noreferrer"
                                                title="purchase orders"
                                                className="ms-2"
                                            >
                                                <i className="fa fa-shopping-cart text-primary" aria-hidden="true" style={{ fontSize: '14px' }}></i>
                                            </a>
                                        )}
                                    </div>
                                </td>
                                <td className="inline-bom-col-numeric" style={bomCellStyle}>{entry.qrBom}</td>
                                <td className="inline-bom-col-numeric" style={bomCellStyle}>
                                    <span
                                        className={entry.qtyAvail < 0 ? 'bg-danger text-white px-2 py-1 rounded' : ''}
                                        style={{
                                            display: 'inline-block',
                                            minWidth: '70px',
                                            textAlign: 'center',
                                            fontSize: '1.1rem',
                                            padding: '6px 10px'
                                        }}
                                    >
                                        {entry.qtyAvail}
                                    </span>
                                </td>
                                <td className="inline-bom-col-numeric exclude-col" style={{ ...bomCellStyle }}>
                                    {entry.qtyAvail < 0 ? (
                                        <div className="d-flex align-items-center justify-content-center" style={{ gap: '8px' }}>
                                            <button
                                                type="button"
                                                className="btn btn-sm"
                                                onClick={() => {
                                                    setPendingExcludedPartsForOrder((prev) => ({
                                                        ...prev,
                                                        [entry.prcpart]: !prev[entry.prcpart],
                                                    }));
                                                }}
                                                title="Select/deselect exclusion"
                                                style={{
                                                    width: '16px',
                                                    height: '16px',
                                                    minWidth: '16px',
                                                    padding: 0,
                                                    borderRadius: '50%',
                                                    border: '1px solid #0d6efd',
                                                    backgroundColor: pendingExcludedPartsForOrder[entry.prcpart] ? '#0d6efd' : 'white',
                                                    lineHeight: 1,
                                                    fontSize: '10px',
                                                    color: 'transparent'
                                                }}
                                            >
                                                &nbsp;
                                            </button>
                                            <button
                                                type="button"
                                                className="btn btn-outline-success btn-sm"
                                                style={{ minWidth: '34px', height: '20px', padding: '0 6px', lineHeight: 1, fontSize: '10px', cursor: 'pointer' }}
                                                title="Save exclusion"
                                                disabled={!onToggleExcludedAvailabilityPart || !!excludedPartsForOrder[entry.prcpart] === !!pendingExcludedPartsForOrder[entry.prcpart]}
                                                onClick={() => {
                                                    if (!onToggleExcludedAvailabilityPart) return;
                                                    onToggleExcludedAvailabilityPart(orderKey, entry.prcpart, !!pendingExcludedPartsForOrder[entry.prcpart]);
                                                }}
                                            >
                                                Save
                                            </button>
                                        </div>
                                    ) : (
                                        <span className="text-muted">-</span>
                                    )}
                                </td>
                                <td className="inline-bom-col-numeric" style={bomCellStyle}>
                                    {renderAggregatedArchiveCell(entry)}
                                </td>
                            </tr>
                        );
                    })
                ) : (
                    <tr>
                        <td colSpan="10" className="text-center py-3">
                            <div className="text-muted">
                                <i className="fa fa-info-circle me-2"></i>
                                No BOM parts available
                            </div>
                        </td>
                    </tr>
                )}
            </tbody>
        </table>
    );

    const renderOrderLines = () => (
        <div className={`table-responsive inline-order-lines-view ${sortedOrderDetails?.length === 1 ? 'dropup-container' : ''}`} style={{ backgroundColor: '#ffffff', minWidth: '900px', overflowX: 'auto' }}>
            <table className={`${tableClassName} gf-table inline-order-lines-compact-table`} style={{ minWidth: '900px', tableLayout: 'fixed', width: '100%' }}>
                <thead className="table-light inline-order-lines-compact-thead">
                    <tr className="text-center">
                        <th style={{ width: '60px', minWidth: '60px', maxWidth: '60px', cursor: 'pointer', ...sharedHeaderStyle }} onClick={() => handleOrderLinesSort('line')}>Line{getOrderLinesSortIcon('line')}</th>
                        <th style={{ width: '180px', minWidth: '180px', maxWidth: '180px', cursor: 'pointer', ...sharedHeaderStyle }} onClick={() => handleOrderLinesSort('part')}>Part{getOrderLinesSortIcon('part')}</th>
                        <th style={{ width: '100px', minWidth: '100px', maxWidth: '100px', cursor: 'pointer', ...sharedHeaderStyle }} onClick={() => handleOrderLinesSort('shipDate')}>Ship Date{getOrderLinesSortIcon('shipDate')}</th>
                        <th style={{ width: '80px', minWidth: '80px', maxWidth: '80px', cursor: 'pointer', ...sharedHeaderStyle }} onClick={() => handleOrderLinesSort('shipQty')}>Ship Qty{getOrderLinesSortIcon('shipQty')}</th>
                        <th style={{ width: '60px', minWidth: '60px', maxWidth: '60px', cursor: 'pointer', ...sharedHeaderStyle }} onClick={() => handleOrderLinesSort('qoh')}>QOH{getOrderLinesSortIcon('qoh')}</th>
                        <th style={{ width: '70px', minWidth: '70px', maxWidth: '70px', cursor: 'pointer', ...sharedHeaderStyle }} onClick={() => handleOrderLinesSort('qoo')}>QOO{getOrderLinesSortIcon('qoo')}</th>
                        <th style={{ width: '50px', minWidth: '50px', maxWidth: '50px', cursor: 'pointer', ...sharedHeaderStyle }} onClick={() => handleOrderLinesSort('qr')}>QR{getOrderLinesSortIcon('qr')}</th>
                        <th style={{ width: '90px', minWidth: '90px', maxWidth: '90px', cursor: 'pointer', ...sharedHeaderStyle }} onClick={() => handleOrderLinesSort('qtyAvail')}>Qty Avail{getOrderLinesSortIcon('qtyAvail')}</th>
                        <th style={{ width: '100px', minWidth: '100px', maxWidth: '100px', ...sharedHeaderStyle }}>
                            {renderLineStatusHeaderControls()}
                        </th>
                        <th style={{ width: '80px', minWidth: '80px', maxWidth: '80px', ...sharedHeaderStyle }}>Action</th>
                        <th style={{ width: '95px', minWidth: '95px', maxWidth: '95px', ...sharedHeaderStyle }}>Archive Item</th>
                        <th style={{ width: '70px', minWidth: '70px', maxWidth: '70px', ...sharedHeaderStyle }}>CDCR</th>
                    </tr>
                </thead>
                <tbody>
                    {visibleOrderDetails?.map((item, index) => {
                        const qoh = Number(item.part?.qoh_cache) || 0;
                        const qoo = Number(item.part?.qoo_cache) || 0;
                        const qr = Number(item.part?.qr_cache) || 0;
                        const qtyInHand = qoh - qr;
                        const key = `${orderNum}-${index}`;
                        const isExpanded = expandedInlineBomRows?.[key];
                        const bomParts = item?.bom?.bomParts || [];

                        const rowBgDual = shouldRowBeGreenInline(item) ? '#d4edda' : '#E6F2FF';

                        return (
                            <React.Fragment key={index}>
                                <tr className="text-center" style={{ backgroundColor: rowBgDual }}>
                                    <td>{item.lineitem}</td>
                                    <td>
                                        <div className="d-flex justify-content-between align-items-center">
                                            <span>
                                                {(() => {
                                                    const partNumber = String(item.prcpart || '');
                                                    return partNumber.length >= 3 ? partNumber.substring(3) : partNumber || 'N/A';
                                                })()}
                                            </span>
                                            <div className="d-inline-block">
                                                <button
                                                    ref={el => partTriggerRefs.current[item.id] = el}
                                                    className="btn btn-sm btn-outline-primary"
                                                    type="button"
                                                    title="View Part Details"
                                                    style={{
                                                        padding: '2px 6px',
                                                        borderRadius: '4px',
                                                        border: '1px solid #0078d4',
                                                        backgroundColor: '#f8f9fa',
                                                        fontSize: '12px'
                                                    }}
                                                    onClick={(e) => togglePartDropdown(item.id, e)}
                                                >
                                                    <i className="fa fa-link" aria-hidden="true" style={{ fontSize: '0.8rem', color: '#0078d4' }}></i>
                                                </button>
                                            </div>
                                            {renderPartDropdown(item)}
                                        </div>
                                    </td>
                                    <td>{item.promisedate || item.promisedate}</td>
                                    <td>{formatQtyDisplay(item.balancedue)}</td>
                                    <td>{qoh}</td>
                                    <td>
                                        <div className="d-flex align-items-center justify-content-center">
                                            <span>{qoo}</span>
                                            {qoo > 0 && (
                                                <a
                                                    href={`https://gigaflightinc.cetecerp.com/part/${item.part?.id}/pos`}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    title="purchase orders"
                                                    className="ms-2"
                                                >
                                                    <i className="fa fa-shopping-cart text-primary" aria-hidden="true" style={{ fontSize: '14px' }}></i>
                                                </a>
                                            )}
                                        </div>
                                    </td>
                                    <td>{qr}</td>
                                    <td>
                                        <span className={qtyInHand < 0 ? 'bg-danger text-white px-2 py-1 rounded' : ''}>
                                            {qtyInHand}
                                        </span>
                                    </td>
                                    <td>
                                        <div className="d-flex align-items-center gap-2 justify-content-center">
                                            <div className="line-status-dropdown position-relative flex-grow-1" style={{ fontSize: '1.05rem' }}>
                                                <div
                                                    ref={el => lineStatusTriggerRefs.current[item.id] = el}
                                                    className="form-control d-flex align-items-center justify-content-between"
                                                    style={{
                                                        cursor: 'pointer',
                                                        minHeight: '38px',
                                                        backgroundColor: 'white',
                                                        border: '1px solid #ced4da',
                                                        fontSize: '12px'
                                                    }}
                                                    onClick={() => onToggleLineStatusDropdown(`${item.id}`)}
                                                >
                                                    <div className="d-flex flex-wrap gap-1">
                                                        {renderStatusBadges(item.id)}
                                                    </div>
                                                </div>
                                            </div>
                                            {renderStatusDropdown(item.id)}
                                            {renderLinePresence?.(item.id)}

                                            {!bulkModeEnabled && hasLineChangesToSubmit(`${item.id}`) && (
                                                <button
                                                    type="button"
                                                    className="btn btn-success btn-sm line-status-submit-btn"
                                                    style={{
                                                        minWidth: "18px",
                                                        height: "18px",
                                                        padding: "0",
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent: "center",
                                                        fontSize: "8px",
                                                        borderRadius: "2px"
                                                    }}
                                                    disabled={Boolean(lineStatusLockOwner?.(item.id))}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        onHandleLineStatusSubmit(`${item.id}`);
                                                    }}
                                                    title={
                                                        lineStatusLockOwner?.(item.id)
                                                            ? `${lineStatusLockOwner(item.id).name} is editing this line status`
                                                            : "Submit line status changes"
                                                    }
                                                >
                                                    ✓
                                                </button>
                                            )}
                                        </div>
                                    </td>
                                    <td>
                                        <button
                                            className="btn btn-sm btn-outline-secondary"
                                            onClick={() => onToggleInlineBomRow(orderNum, index)}
                                            title="Show/Hide BOM"
                                            style={{
                                                padding: '2px 6px',
                                                fontSize: '12px'
                                            }}
                                        >
                                            {expandedInlineBomRows?.[key] ?
                                                <i className="fa-solid fa-caret-up" aria-hidden="true"></i> :
                                                <i className="fa-solid fa-caret-down" aria-hidden="true"></i>
                                            }
                                        </button>
                                    </td>
                                    <td>
                                        <button
                                            type="button"
                                            className="btn btn-link p-0"
                                            onClick={() => onArchiveOrderLine && onArchiveOrderLine(item)}
                                            title="Archive this line item"
                                            disabled={!onArchiveOrderLine}
                                            style={{
                                                fontSize: '1.05rem',
                                                color: '#6c757d',
                                                textDecoration: 'none',
                                                opacity: onArchiveOrderLine ? 1 : 0.5,
                                                cursor: onArchiveOrderLine ? 'pointer' : 'not-allowed'
                                            }}
                                        >
                                            <i className="fa fa-paper-plane" aria-hidden="true"></i>
                                        </button>
                                    </td>
                                    <td>{renderCdcrCell(item)}</td>
                                </tr>

                                {isExpanded && (
                                            <tr style={{ width: '100%' }}>
                                                <td colSpan="12"className="bg-transparent" style={{ width: '100%', padding: '0', border: 'none' }}>
                                                    <div className="px-2 py-2" style={{ width: '100%', boxSizing: 'border-box' }}>
                                                        <strong>BOM Details:</strong>
                                                        {bomParts.length > 0 ? (
                                                            renderBomDetails(bomParts)
                                                        ) : (
                                                            <p className="text-muted mt-2">No BOM parts available</p>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                )}
                            </React.Fragment>
                        );
                    })}
                </tbody>
            </table>
            {renderBulkLineStatusDropdown()}
        </div>
    );

    const renderBomPartsOnly = () => (
        <div className="table-responsive">
            {renderAggregatedBomTable(tableClassName)}
        </div>
    );

    const renderDualView = () => (
        <div className="row inline-dual-view-mode">
            <div className="col-6">
                <h6 className="mb-3 text-primary">Order Lines</h6>
                <div className={`table-responsive inline-dual-order-lines-scroll ${sortedOrderDetails?.length === 1 ? 'dropup-container' : ''}`} style={{ backgroundColor: '#ffffff', overflowX: 'auto' }}>
                    <table
                        className={`${dualViewOrderTableClassName} gf-table`}
                        style={{ fontSize: '1.05rem', width: '100%', minWidth: '900px', tableLayout: 'fixed' }}
                    >
                        <colgroup>
                            <col style={{ width: '5%' }} />
                            <col style={{ width: '11%' }} />
                            <col style={{ width: '9%' }} />
                            <col className="order-line-col-ship-qty" style={{ width: '68px' }} />
                            <col className="order-line-col-qty-equal" style={{ width: '68px' }} />
                            <col className="order-line-col-qty-equal" style={{ width: '68px' }} />
                            <col className="order-line-col-qty-equal" style={{ width: '68px' }} />
                            <col className="order-line-col-qty-equal" style={{ width: '68px' }} />
                            <col style={{ width: '24%' }} />
                            <col style={{ width: '58px' }} />
                            <col className="order-line-col-archive-header" style={{ width: '90px' }} />
                            <col style={{ width: '64px' }} />
                        </colgroup>
                        <thead className="table-light">
                            <tr className="text-center">
                                <th className="order-line-col order-line-col-small" style={{ ...dualViewOrderLineHeaderStyle('5%'), cursor: 'pointer' }} onClick={() => handleOrderLinesSort('line')}>Line{getOrderLinesSortIcon('line')}</th>
                                <th className="order-line-col order-line-col-wide" style={{ ...dualViewOrderLineHeaderStyle('11%'), cursor: 'pointer' }} onClick={() => handleOrderLinesSort('part')}>Part{getOrderLinesSortIcon('part')}</th>
                                <th className="order-line-col order-line-col-date" style={{ ...dualViewOrderLineHeaderStyle('9%'), cursor: 'pointer' }} onClick={() => handleOrderLinesSort('shipDate')}>Ship Date{getOrderLinesSortIcon('shipDate')}</th>
                                <th className="order-line-col order-line-col-ship-qty" style={{ ...dualViewOrderLineHeaderStyle(), cursor: 'pointer' }} onClick={() => handleOrderLinesSort('shipQty')}>Ship<br />Qty{getOrderLinesSortIcon('shipQty')}</th>
                                <th className="order-line-col order-line-col-qty-equal" style={{ ...dualViewOrderLineHeaderStyle(), cursor: 'pointer' }} onClick={() => handleOrderLinesSort('qoh')}>QOH{getOrderLinesSortIcon('qoh')}</th>
                                <th className="order-line-col order-line-col-qty-equal" style={{ ...dualViewOrderLineHeaderStyle(), cursor: 'pointer' }} onClick={() => handleOrderLinesSort('qoo')}>QOO{getOrderLinesSortIcon('qoo')}</th>
                                <th className="order-line-col order-line-col-qty-equal" style={{ ...dualViewOrderLineHeaderStyle(), cursor: 'pointer' }} onClick={() => handleOrderLinesSort('qr')}>QR{getOrderLinesSortIcon('qr')}</th>
                                <th className="order-line-col order-line-col-qty-equal" style={{ ...dualViewOrderLineHeaderStyle(), cursor: 'pointer' }} onClick={() => handleOrderLinesSort('qtyAvail')}>Qty<br />Avail{getOrderLinesSortIcon('qtyAvail')}</th>
                                <th className="order-line-col order-line-col-status" style={dualViewOrderLineHeaderStyle('24%')}>
                                    {renderLineStatusHeaderControls()}
                                </th>
                                <th className="order-line-col order-line-col-action" style={dualViewOrderLineHeaderStyle('7%')}>Action</th>
                                <th className="order-line-col order-line-col-archive-header" style={dualViewOrderLineHeaderStyle()}>Archive</th>
                                <th className="order-line-col" style={dualViewOrderLineHeaderStyle()}>CDCR</th>
                            </tr>
                        </thead>
                        <tbody>
                            {visibleOrderDetails?.map((item, index) => {
                                const qoo = Number(item.part?.qoo_cache) || 0;
                                const qoh = Number(item.part?.qoh_cache) || 0;
                                const qr = Number(item.part?.qr_cache) || 0;
                                const qtyInHand = qoh - qr;
                                const key = `${orderNum}-${index}`;
                                const isExpanded = expandedInlineBomRows?.[key];
                                const bomParts = item?.bom?.bomParts || [];
                                
                                // Use local inline check function that works with orderDetails
                                const rowBackgroundColor = shouldRowBeGreenInline(item) ? '#d4edda' : '#E6F2FF';

                                return (
                                    <React.Fragment key={index}>
                                        <tr className="text-center" style={{ backgroundColor: rowBackgroundColor }}>
                                            <td className="order-line-col order-line-col-small" style={{ ...orderLineCellStyle, width: '5%' }}>{item.lineitem}</td>
                                            <td className="order-line-col order-line-col-wide" style={{ ...orderLineCellStyle, width: '11%', minWidth: 0, overflow: 'hidden' }}>
                                                <div className="d-flex align-items-center gap-1 order-line-part-cell">
                                                    <span className="order-line-part-text">
                                                        {formatPartNumber(item.prcpart)}
                                                    </span>
                                                    <div className="d-inline-block order-line-part-link flex-shrink-0">
                                                        <button
                                                            ref={el => partTriggerRefs.current[item.id] = el}
                                                            className="btn btn-sm btn-outline-primary"
                                                            type="button"
                                                            title="View Part Details"
                                                            style={{
                                                                padding: '2px 6px',
                                                                borderRadius: '4px',
                                                                border: '1px solid #0078d4',
                                                                backgroundColor: '#f8f9fa',
                                                                fontSize: '13px'
                                                            }}
                                                            onClick={(e) => togglePartDropdown(item.id, e)}
                                                        >
                                                            <i className="fa fa-link" aria-hidden="true" style={{ fontSize: '0.75rem', color: '#0078d4' }}></i>
                                                        </button>
                                                    </div>
                                                    {renderPartDropdown(item)}
                                                </div>
                                            </td>
                                            <td className="order-line-col order-line-col-date" style={{ ...orderLineCellStyle, width: '9%' }}>{item.promisedate}</td>
                                            <td className="order-line-col order-line-col-ship-qty" style={orderLineCellStyle}>{formatQtyDisplay(item.balancedue)}</td>
                                            <td className="order-line-col order-line-col-qty-equal" style={orderLineCellStyle}>{qoh}</td>
                                            <td className="order-line-col order-line-col-qty-equal" style={orderLineCellStyle}>{qoo}</td>
                                            <td className="order-line-col order-line-col-qty-equal" style={orderLineCellStyle}>{qr}</td>
                                            <td className="order-line-col order-line-col-qty-equal" style={orderLineCellStyle}>
                                                <span className={qtyInHand < 0 ? 'bg-danger text-white px-2 py-1 rounded' : 'px-2 py-1'} style={{ fontSize: '1rem' }}>
                                                    {qtyInHand}
                                                </span>
                                            </td>
                                            <td className="order-line-col order-line-col-status" style={{ ...orderLineCellStyle, width: '24%', minWidth: 0, overflow: 'hidden' }}>
                                                <div
                                                    className="inline-dual-line-status-row"
                                                    style={{
                                                        display: 'grid',
                                                        gridTemplateColumns: 'minmax(0, 1fr) auto',
                                                        alignItems: 'start',
                                                        gap: '4px',
                                                        width: '100%',
                                                        minWidth: 0,
                                                        maxWidth: '100%'
                                                    }}
                                                >
                                                    <div className="line-status-dropdown position-relative inline-dual-line-status-dropdown" style={{ fontSize: '0.95rem', minWidth: 0, maxWidth: '100%', overflow: 'hidden' }}>
                                                        <div
                                                            ref={el => lineStatusTriggerRefs.current[item.id] = el}
                                                            className="form-control d-flex align-items-start inline-dual-line-status-box"
                                                            style={{
                                                                cursor: 'pointer',
                                                                minHeight: '36px',
                                                                backgroundColor: 'white',
                                                                border: '1px solid #ced4da',
                                                                fontSize: '0.95rem',
                                                                width: '100%',
                                                                maxWidth: '100%',
                                                                minWidth: 0,
                                                                overflowX: 'hidden',
                                                                boxSizing: 'border-box'
                                                            }}
                                                            onClick={() => onToggleLineStatusDropdown(`${item.id}`)}
                                                        >
                                                            <div className="d-flex flex-wrap gap-1 inline-dual-line-status-tags" style={{ width: '100%', minWidth: 0 }}>
                                                                {renderStatusBadges(item.id)}
                                                            </div>
                                                        </div>
                                                    </div>
                                                    {renderStatusDropdown(item.id)}
                                                    {renderLinePresence?.(item.id)}

                                                    {!bulkModeEnabled && hasLineChangesToSubmit(`${item.id}`) && (
                                                        <button
                                                            type="button"
                                                            className="btn btn-success btn-sm line-status-submit-btn"
                                                            style={{
                                                                minWidth: "14px",
                                                                height: "14px",
                                                                padding: "0",
                                                                display: "flex",
                                                                alignItems: "center",
                                                                justifyContent: "center",
                                                                fontSize: "8px",
                                                                borderRadius: "2px"
                                                            }}
                                                            disabled={Boolean(lineStatusLockOwner?.(item.id))}
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                onHandleLineStatusSubmit(`${item.id}`);
                                                            }}
                                                            title={
                                                                lineStatusLockOwner?.(item.id)
                                                                    ? `${lineStatusLockOwner(item.id).name} is editing this line status`
                                                                    : "Submit line status changes"
                                                            }
                                                        >
                                                            ✓
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="order-line-col order-line-col-action" style={{ ...orderLineCellStyle, width: '7%' }}>
                                                <button
                                                    className="btn btn-sm btn-outline-secondary"
                                                    onClick={() => onToggleInlineBomRow(orderNum, index)}
                                                    title="Show/Hide BOM"
                                                    style={{
                                                        padding: '2px 6px',
                                                        fontSize: '12px'
                                                    }}
                                                >
                                                    {expandedInlineBomRows?.[key] ?
                                                        <i className="fa-solid fa-caret-up" aria-hidden="true"></i> :
                                                        <i className="fa-solid fa-caret-down" aria-hidden="true"></i>
                                                    }
                                                </button>
                                            </td>
                                            <td className="order-line-col order-line-col-archive" style={orderLineCellStyle}>
                                                <button
                                                    type="button"
                                                    className="btn btn-link p-0"
                                                    onClick={() => onArchiveOrderLine && onArchiveOrderLine(item)}
                                                    title="Archive this line item"
                                                    disabled={!onArchiveOrderLine}
                                                    style={{
                                                        fontSize: '1.05rem',
                                                        color: '#6c757d',
                                                        textDecoration: 'none',
                                                        opacity: onArchiveOrderLine ? 1 : 0.5,
                                                        cursor: onArchiveOrderLine ? 'pointer' : 'not-allowed'
                                                    }}
                                                >
                                                    <i className="fa fa-paper-plane" aria-hidden="true"></i>
                                                </button>
                                            </td>
                                            <td className="order-line-col" style={orderLineCellStyle}>{renderCdcrCell(item)}</td>
                                        </tr>

                                        {isExpanded && (
                                            <tr style={{ width: '100%' }}>
                                                <td colSpan="12"className="bg-transparent" style={{ width: '100%', padding: '0', border: 'none' }}>
                                                    <div className="px-2 py-2" style={{ width: '100%', boxSizing: 'border-box' }}>
                                                        <strong>BOM Details:</strong>
                                                        {bomParts.length > 0 ? (
                                                            renderBomDetails(bomParts)
                                                        ) : (
                                                            <p className="text-muted mt-2">No BOM parts available</p>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                );
                            })}
                        </tbody>
                    </table>
                    {renderBulkLineStatusDropdown()}
                </div>
            </div>
            <div className="col-6">
                <h6 className="mb-3 text-info">BOM Parts {hasBomParts ? `(${bomPartsCount})` : ''}</h6>
                <div className="table-responsive" style={{ backgroundColor: '#ffffff' }}>
                    {renderAggregatedBomTable(dualViewBomTableClassName)}
                </div>
            </div>
        </div>
    );

    const expandCellStyle = fullWidthExpandRow
        ? {
            display: 'block',
            width: expandRowMinWidth,
            minWidth: '100%',
            maxWidth: 'none',
            padding: 0,
            background: '#fff',
            verticalAlign: 'top',
            borderLeft: 0,
            borderRight: 0,
            boxSizing: 'border-box',
        }
        : { width: '100%', padding: 0 };

    return (
        <tr className={rowClassName || undefined} style={fullWidthExpandRow ? { background: 'var(--bridge-surface, #fff)' } : undefined}>
            <td colSpan={dropdownColSpan} className="p-0" style={expandCellStyle}>
                <div className={containerClassName} style={{ width: '100%', maxWidth: '100%', background: 'var(--bridge-surface, #fff)', color: 'var(--bridge-text, #212529)', margin: fullWidthExpandRow ? 0 : undefined }}>
                    <div className="row border-bottom bg-light" style={{ padding: '4px 12px' }}>
                        <div className="col-12">
                            {/* Single line header with all elements - large spacing between them */}
                            <div className="d-flex align-items-center justify-content-between" style={{ flexWrap: 'nowrap', overflowX: 'auto', width: '100%' }}>
                                <div style={{ whiteSpace: 'nowrap' }}>
                                    <b>Order Num</b>: <a 
                                        href={`https://gigaflightinc.cetecerp.com/order/MN${orderNum}/view`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        style={{ textDecoration: 'none' }}
                                    >
                                        {orderNum}
                                    </a>
                                    <a
                                        href={orderFolderLink || '#'}
                                        onClick={(event) => {
                                            if (!orderFolderLink) {
                                                event.preventDefault();
                                            }
                                        }}
                                        target={orderFolderLink ? '_blank' : undefined}
                                        rel={orderFolderLink ? 'noreferrer' : undefined}
                                        style={{ 
                                            cursor: orderFolderLink ? 'pointer' : 'not-allowed',
                                            textDecoration: 'none',
                                            marginLeft: '4px'
                                        }}
                                        title={orderFolderLink ? `Open folder: ${orderFolderLink}` : 'No folder link saved yet'}
                                    >
                                        <i className="fa fa-folder" aria-hidden="true" style={{ fontSize: '16px', color: orderFolderLink ? '#ff8c00' : '#cccccc' }}></i>
                                    </a>
                                </div>
                                
                                <div style={{ whiteSpace: 'nowrap' }}>
                                    <b>WO#</b>: <a 
                                        href={`https://gigaflightinc.cetecerp.com/order/MN${line.order?.woNum}/view`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        style={{ textDecoration: 'none' }}
                                    >
                                        {line.order?.woNum || 'N/A'}
                                    </a>
                                    {line.order?.woNum && line.order.woNum !== '-' && line.order.woNum !== 'N/A' && (
                                        <a
                                            href={line.order?.woUrl || '#'}
                                            onClick={(event) => {
                                                if (!line.order?.woUrl) {
                                                    event.preventDefault();
                                                }
                                            }}
                                            target={line.order?.woUrl ? '_blank' : undefined}
                                            rel={line.order?.woUrl ? 'noreferrer' : undefined}
                                            style={{ 
                                                cursor: line.order?.woUrl ? 'pointer' : 'not-allowed',
                                                textDecoration: 'none',
                                                marginLeft: '4px'
                                            }}
                                            title={line.order?.woUrl ? `Open WO folder: ${line.order.woUrl}` : 'No WO folder link available'}
                                        >
                                            <i className="fa fa-folder" aria-hidden="true" style={{ fontSize: '16px', color: line.order?.woUrl ? (isOrange ? '#cc7a00' : '#ff8c00') : '#cccccc' }}></i>
                                        </a>
                                    )}
                                </div>
                                
                                <div style={{ whiteSpace: 'nowrap' }}>
                                    <b>PO Num</b>: {line.order?.custponum || 'N/A'}
                                </div>
                                
                                {/* Comments Section - positioned between Order # and Customer Name */}
                                <div className="d-flex align-items-center" style={{ maxWidth: '180px', minWidth: '120px', flexShrink: 0, gap: '4px' }}>
                                    <div style={{ flex: 1, minWidth: '100px' }}>
                                        <textarea
                                            style={{
                                                width: '100%',
                                                minHeight: '32px',
                                                maxHeight: '50px',
                                                overflowY: 'auto',
                                                resize: 'none',
                                                fontSize: '0.7rem',
                                                padding: '3px 5px',
                                                lineHeight: '1.3',
                                                margin: 0
                                            }}
                                            value={latestComment}
                                            className="form-control"
                                            rows={2}
                                            readOnly
                                            placeholder="No comments"
                                        />
                                    </div>
                                    <div style={{ flexShrink: 0 }}>
                                        <HoverPO
                                            orderLineId={firstLineId}
                                            comments={aggregatedComments}
                                            orderNumber={orderNum}
                                            sourcePage="orders"
                                            onDeleteComment={handleInlineDeleteComment}
                                            onResolveThread={handleInlineResolveThread}
                                            onAddComment={handleInlineAddComment}
                                        >
                                            <button
                                                title="Click to view/add notes"
                                                type="button"
                                                className="btn btn-light"
                                                style={{ 
                                                    padding: "2px 4px", 
                                                    borderRadius: "50%", 
                                                    border: "1px solid #ccc",
                                                    fontSize: "8px",
                                                    width: "20px",
                                                    height: "20px",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    margin: 0
                                                }}
                                            >
                                                <svg xmlns="http://www.w3.org/2000/svg" height="10" viewBox="0 0 512 512">
                                                    <path
                                                        fill="currentColor"
                                                        d="M344 0L488 0c13.3 0 24 10.7 24 24l0 144c0 9.7-5.8 18.5-14.8 22.2s-19.3 1.7-26.2-5.2l-39-39-87 87c-9.4 9.4-24.6 9.4-33.9 0l-32-32c-9.4 9.4-9.4 24.6 0-33.9l87-87L327 41c-6.9-6.9-8.9-17.2-5.2-26.2S334.3 0 344 0zM168 512L24 512c-13.3 0-24-10.7-24-24L0 344c0-9.7 5.8-18.5 14.8-22.2s19.3-1.7 26.2 5.2l39 39 87-87c9.4-9.4 24.6-9.4 33.9 0l32 32c9.4 9.4 9.4 24.6 0 33.9l-87 87 39 39c6.9 6.9 8.9 17.2 5.2 26.2s-12.5 14.8-22.2 14.8z"
                                                    />
                                                </svg>
                                            </button>
                                        </HoverPO> 
                                    </div>
                                </div>
                                
                                <div className="d-flex align-items-center" style={{ gap: '12px' }}>
                                    <a
                                        href="https://aeroplicity.com/file-share/items/Departments/Engineering/Drawings?view=all"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="btn btn-primary btn-sm"
                                        style={{
                                            fontSize: '0.875rem',
                                            padding: '6px 12px',
                                            borderRadius: '4px',
                                            textDecoration: 'none',
                                            whiteSpace: 'nowrap'
                                        }}
                                        title="Engineering drawings (Aeroplicity)"
                                    >
                                        Eng drawing
                                    </a>

                                    <a
                                        href="https://gigaflightinc.cetecerp.com/widget/generic?name=enter_build_for_stock_order"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="btn btn-primary btn-sm"
                                        style={{
                                            fontSize: '0.875rem',
                                            padding: '6px 12px',
                                            borderRadius: '4px',
                                            textDecoration: 'none',
                                            whiteSpace: 'nowrap'
                                        }}
                                        title="Open Build Part"
                                    >
                                        Build part
                                    </a>
                                </div>
                                
                                <div style={{ whiteSpace: 'nowrap' }}>
                                    <b>Customer Name</b>: {line.order?.customer?.id ? (
                                        <a 
                                            href={`https://gigaflightinc.cetecerp.com/customer/${line.order.customer.id}/view`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            style={{ textDecoration: 'none' }}
                                        >
                                            {line.order.customer.name || 'N/A'}
                                        </a>
                                    ) : (
                                        line.order?.customer?.name || 'N/A'
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="d-flex justify-content-between align-items-center border-bottom bg-white">
                        <div className="d-flex">
                            <button
                                className={`px-4 py-3 border-0 bg-transparent ${inlineActiveView?.[orderNum] === 'orderLines' ? 'border-bottom border-primary text-primary fw-bold' : 'text-muted'}`}
                                onClick={() => onInlineViewChange(orderNum, 'orderLines')}
                                style={{
                                    borderBottom: inlineActiveView?.[orderNum] === 'orderLines' ? '3px solid #0078d4' : '3px solid transparent'
                                }}
                            >
                                Order Lines
                            </button>
                            <button
                                className={`px-4 py-3 border-0 bg-transparent ${inlineActiveView?.[orderNum] === 'bomParts' ? 'border-bottom border-primary text-primary fw-bold' : 'text-muted'}`}
                                onClick={() => onInlineViewChange(orderNum, 'bomParts')}
                                style={{
                                    borderBottom: inlineActiveView?.[orderNum] === 'bomParts' ? '3px solid #0078d4' : '3px solid transparent'
                                }}
                            >
                                BOM Parts
                            </button>
                        </div>

                        <div className="px-3 d-flex align-items-center gap-2">
                            <button
                                className="btn btn-outline-success btn-sm d-flex align-items-center gap-1"
                                onClick={handleExportOrderLines}
                                title="Export Order Lines to Excel"
                                style={{ fontSize: '0.8rem', padding: '5px 10px' }}
                            >
                                <i className="fa fa-file-excel-o" aria-hidden="true"></i>
                                <span>Export Lines</span>
                            </button>
                            <button
                                className="btn btn-outline-success btn-sm d-flex align-items-center gap-1"
                                onClick={handleExportBomParts}
                                title="Export BOM Parts to Excel"
                                style={{ fontSize: '0.8rem', padding: '5px 10px' }}
                            >
                                <i className="fa fa-file-excel-o" aria-hidden="true"></i>
                                <span>Export BOM</span>
                            </button>
                            <button
                                className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-2"
                                onClick={() => onInlineSwitchView(orderNum)}
                                style={{
                                    fontSize: '0.875rem',
                                    padding: '6px 12px',
                                    borderRadius: '4px'
                                }}
                            >
                                Switch View
                            </button>
                            <button
                                className={`btn btn-sm d-flex align-items-center gap-2 ${inlineActiveView?.[orderNum] === 'both' ? 'btn-primary' : 'btn-outline-primary'}`}
                                onClick={() => onInlineDualView(orderNum)}
                                style={{
                                    fontSize: '0.875rem',
                                    padding: '6px 12px',
                                    borderRadius: '4px'
                                }}
                            >
                                Dual View
                            </button>
                        </div>
                    </div>

                    <div className={contentPaddingClass}>
                        {inlineActiveView?.[orderNum] === 'orderLines' && renderOrderLines()}

                        {inlineActiveView?.[orderNum] === 'bomParts' && renderBomPartsOnly()}

                        {inlineActiveView?.[orderNum] === 'both' && renderDualView()}
                    </div>
                </div>
                <CdcrModal
                    show={!!cdcrLine}
                    orderLine={cdcrLine}
                    seed={{ customerName: line?.order?.customer?.name }}
                    onClose={() => setCdcrLine(null)}
                />
            </td>
            {Array.from({ length: dropdownTrailingCells }).map((_, idx) => (
                <td
                    key={`inline-dropdown-adjacent-${idx}`}
                    className="p-0 inline-dropdown-adjacent-cell"
                    style={{ verticalAlign: 'top', backgroundColor: 'var(--bridge-surface, #ffffff)' }}
                />
            ))}
        </tr>
    );
};

export default InlineOrderDropdown;

